// Centralised content for the RetrofitIQ website (operated by G.A.S General Building Services Ltd).
// Edit copy / case studies / reviews here to update across the site.

export const BUSINESS = {
  name: "RetrofitIQ",
  shortName: "RetrofitIQ",
  legalName: "G.A.S General Building Services Ltd",
  owner: "George Sora",
  role: "Certified Passive House Designer",
  phone: "0753 824 3578",
  phoneHref: "tel:+447538243578",
  email: "info@retrofitiq.co.uk",
  emailHref: "mailto:info@retrofitiq.co.uk",
  address: "65 Glenavon Road, London E15 4DE",
  tagline: "Investigate • Diagnose • Design • Remediate • Verify",
  positioning:
    "Building Performance Diagnostics — Heat Loss, Condensation, Damp, Air Leakage, Soundproofing, Ventilation & Indoor Air Quality",
  subPositioning:
    "Certified Passive House Designer | One Company. One Process. One Point of Responsibility.",
  subtitle: "Building Performance Diagnostics",
};

// Official RetrofitIQ master brand mark — circular RIQ emblem (red R, green IQ,
// circular arrow ring + house roof, "RetroFIT IQ" wordmark) on white. Single master
// asset used consistently across header, footer and favicons.
export const RETROFITIQ_LOGO = {
  src: "https://customer-assets.emergentagent.com/job_dccf8f76-be29-4f8b-b4a5-a675ec5f8aff/artifacts/pgv7vt7u_WhatsApp%20Image%202026-06-11%20at%2014.26.38.jpeg",
  alt: "RetroFIT IQ — Building Performance Diagnostics",
};
export const RETROFITIQ_LOGO_WHITE = {
  src: "https://customer-assets.emergentagent.com/job_dccf8f76-be29-4f8b-b4a5-a675ec5f8aff/artifacts/pgv7vt7u_WhatsApp%20Image%202026-06-11%20at%2014.26.38.jpeg",
  alt: "RetroFIT IQ — Building Performance Diagnostics",
};
export const RETROFITIQ_WORDMARK = {
  src: "https://customer-assets.emergentagent.com/job_dccf8f76-be29-4f8b-b4a5-a675ec5f8aff/artifacts/pgv7vt7u_WhatsApp%20Image%202026-06-11%20at%2014.26.38.jpeg",
  alt: "RetroFIT IQ — Building Performance Diagnostics",
};

// Public Google Business Profile review URL — set this when GBP is verified.
// When `null`, the "Leave us a Google Review" CTA is hidden site-wide.
export const GOOGLE_REVIEW_URL = "https://g.page/r/CUDM13OmaOXWEBM/review";
// Public Google Business Profile listing URL — set when GBP is verified.
export const GOOGLE_BUSINESS_URL = "https://share.google/uCYHKzgqyeWjTrzxx";

// London service-area pages — each is a real geographic landing page for local SEO.
export const SERVICE_AREAS = [
  {
    slug: "london",
    name: "London",
    intro: "RetrofitIQ provides Building Performance Diagnostics & Remediation across Greater London — Investigate, Diagnose, Design, Remediate and Verify. From the solid-wall Victorian terraces of Hackney and Camden to the warehouse conversions of Wapping and the new-build estates of Stratford and Canary Wharf, we deliver measured, building-physics-led results.",
    boroughs: ["Tower Hamlets", "Hackney", "Islington", "Camden", "Westminster", "Southwark", "Lambeth", "Wandsworth", "Newham", "Greenwich", "Lewisham", "Haringey"],
    postcodeBands: ["E1-E20", "N1-N22", "NW1-NW11", "SE1-SE28", "SW1-SW20", "W1-W14", "WC1-WC2", "EC1-EC4"],
    typicalProperties: ["Victorian terraces", "Edwardian semi-detached", "Inter-war suburban housing", "1960s council blocks", "Modern new-build flats and townhouses"],
    headline: "Building Performance Diagnostics & Remediation — London",
    heatLossIssues: [
      "Single-skin solid masonry walls with U-values around 2.0-2.2 W/m²K — typically 60-70% of the total heat-loss budget",
      "Uninsulated concrete lintels driving cold band around every window",
      "Suspended timber ground floors over uninsulated voids",
      "Sash-window joinery with substantial frame leakage",
      "Lofts with compressed or slumped insulation at the eaves",
    ],
    condensationIssues: [
      "North-facing rooms and corners falling below dewpoint in winter",
      "Single-glazed and early double-glazed bay windows with chronic surface condensation",
      "Cold reveals and lintels driving localised mould around openings",
      "Bathrooms and kitchens with insufficient mechanical extraction",
      "Conversion flats above unheated ground floors — cold floors trap moisture",
    ],
    airtightnessIssues: [
      "Original suspended timber floor perimeters leaking through skirting gaps",
      "Service penetrations through external walls left unsealed",
      "Loft hatches with no seal or insulation",
      "Open chimney flues drawing warm air constantly",
      "Sash-window and door perimeters with degraded weatherstripping",
    ],
    retrofitOpportunities: [
      "Sequenced fabric-first retrofit: air-sealing → insulation → controlled ventilation",
      "EnerPHit-led whole-house design for the most demanding refurbishments",
      "IWI with WUFI-modelled vapour control on Listed and conservation properties",
      "Solid-wall EWI where planning permits",
      "MVHR design and commissioning following blower-door verification",
    ],
  },
  {
    slug: "central-london",
    name: "Central London",
    intro: "Central London — Westminster, Marylebone, Mayfair, Soho, Fitzrovia, Bloomsbury, Holborn, Clerkenwell, the City of London and the South Bank — has some of the most demanding building stock in the UK: Listed Georgian and Victorian period properties, prestige conversions, mansion blocks, and high-spec new-build apartments. Every project here must reconcile heritage constraints with modern building-performance expectations.",
    boroughs: ["City of Westminster", "City of London", "Camden (south)", "Southwark (north)", "Lambeth (north)", "Islington (south)"],
    postcodeBands: ["WC1", "WC2", "EC1", "EC2", "EC3", "EC4", "W1", "SW1"],
    typicalProperties: [
      "Georgian terraces (WC1, W1, WC2)",
      "Victorian and Edwardian mansion blocks (W1, SW1)",
      "Listed and conservation-area properties",
      "Office-to-residential conversions (EC1, EC2)",
      "High-end new-build apartments along the South Bank (SE1)",
    ],
    headline: "Central London Building Performance Diagnostics — Listed & Heritage Specialists",
    heatLossIssues: [
      "Listed mansion blocks with single-skin solid brick walls and no permission for external insulation",
      "Original 6-over-6 sash windows with substantial heat loss around frames",
      "Light wells and back additions trapped between adjoining buildings — chronically cold party walls",
      "Original lath-and-plaster ceilings on top floors with no loft insulation",
      "Steel-framed Edwardian / inter-war mansion blocks with linear thermal-bridge cold bands at every floor slab",
    ],
    condensationIssues: [
      "Mansion-block bedrooms and bathrooms with original single-pane glazing producing chronic winter condensation",
      "Internal courtyards trapping moisture-laden air with no cross-ventilation",
      "Heritage-paint and gypsum-plaster finishes failing where wall surface temperatures fall below dewpoint",
      "Listed-fabric constraints making conventional internal insulation high-risk",
    ],
    airtightnessIssues: [
      "Original cast-iron service risers and chimney flues providing whole-building bypass paths",
      "Floorboard perimeters and panel-skirting gaps in Georgian terraces",
      "Original sash window draughts and unsealed shutter boxes",
      "Pre-1980 lift shafts in mansion blocks acting as continuous stack-effect chimneys",
    ],
    retrofitOpportunities: [
      "Vapour-open IWI with hygrothermally-modelled wood-fibre build-ups on Listed walls",
      "Discreet airtightness sealing on suspended floors, sash-window perimeters and loft hatches",
      "MVHR routed through original service voids and ex-chimney stacks",
      "PHI EnerPHit-led whole-flat refurbishment design for prestige clients",
      "Discreet acoustic floor build-ups in mansion-block conversions",
    ],
  },
  {
    slug: "east-london",
    name: "East London",
    intro: "From the converted Victorian terraces of Walthamstow and Leyton through the warehouse conversions of Hackney Wick and Bow to the new-build estates of Stratford and Canary Wharf — East London has one of the most varied building stocks in the capital. Our diagnostics-led approach matches the remedy to the actual construction, not a generic assumption.",
    boroughs: ["Tower Hamlets", "Hackney", "Newham", "Waltham Forest", "Redbridge", "Barking & Dagenham", "Havering"],
    postcodeBands: ["E1-E20", "IG (Ilford, Woodford, Loughton)", "RM (Romford, Hornchurch, Dagenham)"],
    typicalProperties: [
      "Victorian and Edwardian terraces (E5, E8, E9, E10, E11, E17)",
      "Warehouse and industrial conversions (E1, E2, E3, E9)",
      "Council-built post-war blocks (E2, E3, E14)",
      "Olympic Park new-build flats and townhouses (E15, E20)",
      "Inter-war and 1960s suburban housing (IG, RM)",
    ],
    headline: "East London Building Performance Diagnostics & Retrofit — E, IG, RM",
    heatLossIssues: [
      "Solid-wall Victorian terraces in Stratford, Bethnal Green, Hackney, Leyton and Walthamstow with no cavity to fill",
      "Warehouse conversions with single-glazed industrial windows and no perimeter thermal break",
      "Council-block flats (Tower Hamlets, Newham) with uninsulated concrete soffits and party walls",
      "Olympic-Park-era new-builds with documented airtightness drift and thermal-bridging issues",
      "Suburban semis in Ilford / Romford with original cavity-fill that has slumped over decades",
    ],
    condensationIssues: [
      "Bow and Hackney warehouse conversions with chronic mould on inside faces of single-skin brick walls",
      "High-occupancy HMOs across Newham and Waltham Forest with persistent kitchen and bathroom condensation",
      "North-facing master bedrooms in Olympic Park townhouses with documented surface-temperature drift",
      "Council-block bathrooms with no working extract and persistent ceiling mould",
    ],
    airtightnessIssues: [
      "Warehouse conversions with original cast-iron column-to-floor junctions left open",
      "Loft hatches and roof-to-wall junctions in Victorian terraces (E5, E8, E10)",
      "Service penetrations through external walls in 1960s council blocks",
      "Front-door perimeters in conversion flats with no compression seal",
    ],
    retrofitOpportunities: [
      "Whole-house EnerPHit-style retrofit on Victorian terraces in E5, E8, E17",
      "Airtightness-first remediation programme for warehouse conversion blocks",
      "MVHR retrofit into Olympic Park townhouses to fix documented IAQ issues",
      "Independent retrofit review for Tower Hamlets / Newham social-housing stock",
      "Damp and condensation investigation programmes for Council and HMO landlords",
    ],
  },
  {
    slug: "north-london",
    name: "North London",
    intro: "North London's heavyweight Victorian and Edwardian housing stock — from the bay-windowed terraces of Holloway and Finsbury Park through the substantial detached homes of Highgate and Hampstead Garden Suburb to the inter-war semis of Finchley and Barnet — is well-known for cold floors, single-skin extensions and condensation in north-facing rooms. We use FLIR thermal imaging, blower door testing and moisture diagnostics to identify the actual problem before any insulation or remedial work is specified.",
    boroughs: ["Camden", "Islington", "Haringey", "Enfield", "Barnet"],
    postcodeBands: ["N1-N22", "NW (NW1, NW3, NW5, NW11)", "EN (Enfield)"],
    typicalProperties: [
      "Victorian terraces (N1, N4, N5, N7, N16, N19)",
      "Edwardian semi-detached (N10, N20)",
      "1930s suburban housing (N12, N13, N14)",
      "Hampstead Garden Suburb period properties (NW3, NW11)",
      "Highgate / Muswell Hill substantial detached homes (N6, N10)",
    ],
    headline: "North London Building Performance Diagnostics — N, NW, EN",
    boroughAreas: ["Barnet", "Enfield", "Camden", "Islington", "Haringey", "Finchley", "Highgate", "Muswell Hill", "Wood Green", "Edmonton", "Holloway", "Archway", "Finsbury Park"],
    heatLossIssues: [
      "Victorian solid-wall terraces in Islington, Holloway, Archway and Finsbury Park with high heat loss through original brickwork",
      "Single-storey rear-extension flat roofs in 1930s semis (Finchley, Barnet) with compressed or absent insulation",
      "Original sash-window bays in Hampstead Garden Suburb and Highgate with chronic perimeter draughts",
      "Suspended timber ground floors throughout N4, N5, N16, N19 with no underfloor insulation",
      "Edmonton / Wood Green ex-council blocks with persistent cold-bridging at slab edges",
    ],
    condensationIssues: [
      "North-facing bedrooms and bathrooms in N1, N5, N7 Victorian terraces with chronic winter mould",
      "Camden conversion flats with inadequate kitchen extract and bathroom ventilation",
      "Cold lintel-and-reveal bands above bay windows in Edwardian semis (N10, N20)",
      "Internal-wall-insulation failures in earlier retrofits at conservation properties",
    ],
    airtightnessIssues: [
      "Suspended floor perimeters in Holloway, Finsbury Park and Wood Green Victorian terraces",
      "Loft hatch and party-wall junction leakage in 1930s Finchley / Barnet semis",
      "Sash-window perimeters and shutter boxes in Highgate / Hampstead Garden Suburb",
      "Service-penetration leaks in Edmonton and Wood Green ex-council blocks",
    ],
    retrofitOpportunities: [
      "EnerPHit whole-house refurbishment on Highgate / Hampstead detached homes",
      "IWI with hygrothermal modelling on Islington / Camden conservation terraces",
      "Loft airtightness + insulation upgrade across N12, N13, N14 1930s semis",
      "EWI on permissible Edmonton / Wood Green suburban semis",
      "Independent retrofit assessment for Camden and Islington architects",
    ],
  },
  {
    slug: "south-london",
    name: "South London",
    intro: "Across South London we work on everything from the bay-windowed Victorian terraces of Clapham and Battersea through the Edwardian housing of Dulwich and Forest Hill to the modern apartments of Bermondsey and Bankside. The common thread is solid-wall construction and lightweight conversion flats needing a measured, building-science-led retrofit approach.",
    boroughs: ["Lambeth", "Wandsworth", "Southwark", "Lewisham", "Greenwich", "Bromley", "Croydon", "Sutton", "Merton", "Kingston upon Thames"],
    postcodeBands: ["SE1-SE28", "SW (south)", "CR (Croydon)", "BR (Bromley)", "KT (Kingston)"],
    typicalProperties: [
      "Victorian solid-wall terraces (SW4, SW8, SW11, SE5, SE15)",
      "Edwardian semis (SE15, SE22, SW2, SW12)",
      "Conversion flats over solid-wall ground floors",
      "Post-war estates (SE1, SE16, SE17)",
      "1930s and 1950s suburban housing (CR, BR, SM)",
    ],
    headline: "South London Building Performance Diagnostics — SE, SW, CR, BR",
    boroughAreas: ["Greenwich", "Lewisham", "Croydon", "Bromley", "Lambeth", "Southwark", "Wandsworth", "Wimbledon", "Dulwich", "Peckham", "Brixton", "Clapham"],
    heatLossIssues: [
      "Victorian terrace party walls in Clapham, Brixton, Camberwell and Peckham with no cavity to insulate",
      "Wandsworth / Battersea conversion flats with cold ground-floor suspended timber decks",
      "Dulwich / Forest Hill Edwardian semis with single-storey rear extensions over uninsulated voids",
      "Croydon and Bromley inter-war semis with original cavity-fill that has degraded",
      "Bermondsey / Bankside post-war estates with thermal-bridge cold bands at every floor slab",
    ],
    condensationIssues: [
      "North-facing bedrooms in Brixton, Streatham and Tooting bay-window Victorian terraces",
      "Bathroom and kitchen mould in Wimbledon and Earlsfield conversion flats",
      "Cold-corner mould in 1960s council blocks across Lewisham and Southwark",
      "Original-glazing condensation in Dulwich / East Dulwich heritage properties",
    ],
    airtightnessIssues: [
      "Suspended floor perimeters in SW4, SW8, SW11, SE5 Victorian terraces",
      "Loft hatches and party-wall junctions in Croydon / Sutton 1930s semis",
      "Service penetrations in Bermondsey / Greenwich new-build apartments (documented post-occupancy drift)",
      "Roof-to-wall junctions in Dulwich Edwardian houses",
    ],
    retrofitOpportunities: [
      "Whole-house EnerPHit retrofit on Clapham / Battersea Victorian terraces",
      "Underfloor insulation programmes for SW4, SW11, SE15 suspended floors",
      "IWI design + WUFI modelling for Dulwich / Forest Hill Edwardian properties",
      "Independent retrofit review for Bermondsey and Greenwich new-build apartment blocks",
      "EWI on Croydon and Bromley suburban semis where planning permits",
    ],
  },
  {
    slug: "west-london",
    name: "West London",
    intro: "West London ranges from the period stucco terraces of Notting Hill and Kensington through the inter-war suburbs of Ealing and Acton to the riverside Victorian villas of Chiswick and Hammersmith. We tailor diagnostics and retrofit strategies to whatever the underlying construction actually is — not assumptions made from the front door.",
    boroughs: ["Kensington & Chelsea", "Hammersmith & Fulham", "Ealing", "Hounslow", "Hillingdon", "Brent", "Westminster"],
    postcodeBands: ["W1-W14", "UB (Uxbridge)", "TW (Twickenham, Hounslow)", "HA (Harrow)"],
    typicalProperties: [
      "Period stucco terraces (W2, W8, W11)",
      "Edwardian terraces (W4, W6, W12)",
      "1930s semi-detached (W3, W5, W13)",
      "Riverside new-build apartments (W6, TW8)",
      "Listed and conservation-area properties (W8, W11, W14)",
    ],
    headline: "West London Building Performance Diagnostics — W, UB, TW",
    boroughAreas: ["Ealing", "Hammersmith", "Fulham", "Chiswick", "Acton", "Brentford", "Hounslow", "Richmond", "Kensington", "Chelsea", "Shepherd's Bush", "Notting Hill"],
    heatLossIssues: [
      "Stucco-fronted Listed terraces in Notting Hill, Kensington and Chelsea with no permission for external insulation",
      "Edwardian terrace ground floors in Hammersmith, Fulham and Chiswick over uninsulated suspended timber",
      "1930s semi-detached homes in Acton, Ealing and Hanwell with degraded cavity-fill insulation",
      "Conversion flats with chronically cold rear extensions and cold side-return party walls",
      "Riverside Brentford / Chiswick new-build apartments with documented thermal-bridge issues at floor slabs",
    ],
    condensationIssues: [
      "Notting Hill and Kensington garden-flat bedrooms below ground level with chronic mould",
      "North-facing rooms in Chiswick / Hammersmith Edwardian terraces",
      "Loft conversions in Ealing and Acton with inadequate ventilation and persistent condensation on rafters",
      "Riverside Brentford apartment glazing-to-frame junctions with surface mould",
    ],
    airtightnessIssues: [
      "Stucco-terrace sash-window perimeters and original shutter boxes (W2, W8, W11)",
      "Suspended floor perimeters in Hammersmith, Fulham and Chiswick Edwardian terraces",
      "Loft-conversion roof-to-wall junctions in Ealing and Acton",
      "Service penetrations in riverside Brentford / Chiswick apartment blocks",
    ],
    retrofitOpportunities: [
      "Vapour-open IWI with WUFI modelling on Notting Hill / Kensington Listed terraces",
      "Underfloor insulation and air-sealing on Edwardian Chiswick, Hammersmith and Fulham terraces",
      "EnerPHit-led loft and side-return retrofit on Ealing and Acton 1930s semis",
      "Independent design review for Brentford and Chiswick riverside apartment retrofits",
      "MVHR design and commissioning for West London architects on whole-house refurbishments",
    ],
  },
];

// FAQ blocks per service-slug. Rendered + injected as JSON-LD FAQPage schema for rich Google results.
export const FAQ_BY_SLUG = {
  "heat-loss-investigation": [
    { q: "What is a heat loss investigation?", a: "It's a focused diagnostic survey that uses thermal imaging and surface-temperature measurement to identify exactly where your home is losing heat — walls, glazing, doors, junctions and gaps — so remediation can be targeted at the areas that matter most." },
    { q: "How is it different from a thermal imaging survey?", a: "A thermal imaging survey is the diagnostic tool; a heat loss investigation applies that tool specifically to quantify and prioritise heat loss across the building fabric, then sets out a fabric-first plan to reduce it." },
    { q: "When is the best time to carry one out?", a: "During colder months, when there is a sufficient temperature difference between inside and outside (typically ≥10 °C). This makes heat-loss patterns clearly visible on the thermal image." },
    { q: "Will it tell me how to fix the problem?", a: "Yes. You receive a prioritised, building-physics-led remediation plan — and because we Investigate, Diagnose, Design, Remediate and Verify, the same team can carry out and verify the work." },
  ],
  "thermal-bridging-survey": [
    { q: "What is a thermal bridge?", a: "A thermal bridge is a localised area where heat passes through the building fabric more easily than the surrounding insulation — typically at junctions, corners, lintels and reveals. It creates a cold surface that loses heat and attracts condensation and mould." },
    { q: "Why do thermal bridges cause mould?", a: "Because they are the coldest surfaces in the room. When a surface falls below the dewpoint of the indoor air, water vapour condenses on it. Thermal bridges therefore become the first place surface condensation and black mould appear." },
    { q: "Can thermal bridging move after insulation work?", a: "Yes. Once the original cold area is improved, the next-coldest surface — often a junction or corner — can become the new condensation hotspot. A survey identifies these so remediation is comprehensive." },
    { q: "How do you survey for thermal bridging?", a: "We use calibrated FLIR thermal imaging under a stabilised indoor/outdoor temperature difference, combined with surface-temperature and dewpoint measurement, to locate each bridge and assess its condensation risk." },
  ],
  "thermal-imaging": [
    {
      q: "What is a thermal imaging survey and what can it detect?",
      a: "A thermal imaging (infrared) survey uses a calibrated FLIR camera to measure the surface temperature of walls, ceilings, floors, windows and reveals. It can detect missing or compressed insulation, thermal bridges, air leakage paths, cold reveals at risk of condensation, and the underlying cause of damp or mould patches. The output is a calibrated set of FLIR images with surface temperature readings and a written diagnostic report.",
    },
    {
      q: "How much does a thermal imaging survey cost in London?",
      a: "Pricing depends on the property size and the depth of the report. A focused single-issue thermal scan typically starts from around £250, while a full Home Health Diagnostic Survey (thermal imaging + blower door + smoke tracing + written report with prioritised recommendations) starts from around £600. Every quote is confirmed in writing after a quick scoping call — we do not quote blindly over the phone.",
    },
    {
      q: "When is the best time of year to do a thermal imaging survey?",
      a: "Thermal imaging requires a temperature differential of at least 10 °C between inside and outside, so the heating season — typically October through April in the UK — gives the clearest, most reliable results. We can survey at any time of year but readings taken in mid-winter are the most diagnostic.",
    },
    {
      q: "Do you provide a written report after the survey?",
      a: "Yes. Every thermal imaging survey is delivered with a written report containing the calibrated FLIR images, surface temperature readings, the location and probable cause of each finding, and a prioritised remedial plan. The report is yours to share with insurers, architects or other contractors.",
    },
  ],
  "blower-door-testing": [
    {
      q: "What is a blower door test?",
      a: "A blower door test is a controlled pressurisation/depressurisation test that measures how airtight a building is. A calibrated fan is mounted in an external doorway, all other openings are sealed, and the house is brought to a 50 Pascal pressure difference (50 Pa) against the outside. The fan flow at 50 Pa gives the air change rate — and combined with smoke tracing reveals every air leakage pathway in the envelope.",
    },
    {
      q: "Why does my home need an airtightness test?",
      a: "Uncontrolled air leakage is one of the largest hidden causes of heat loss, cold draughts and condensation in UK homes. Blower door testing quantifies the air leakage and pinpoints exactly where it is happening (floor perimeters, lintels, service penetrations, loft hatches). Without measured data, retrofit insulation decisions are guesswork. Airtightness testing is also a mandatory step on Passive House and EnerPHit projects.",
    },
    {
      q: "How much does a blower door test cost?",
      a: "Standalone blower door testing for a typical London terrace starts from around £450, including the set-up, the calibrated 50 Pa test and a written report with the air change rate (n50/q50) and a smoke-traced leakage map. We offer discounted bundle pricing when blower door testing is combined with thermal imaging in a Home Health Diagnostic Survey.",
    },
    {
      q: "Will the blower door test damage anything in my home?",
      a: "No — blower door testing uses a pressure difference of only 50 Pa, which is roughly the equivalent of a 30 mph wind on the building. It is well within normal structural loading and causes no damage. We do however briefly seal off any open flues and trickle vents during the test, and reset everything afterwards.",
    },
  ],
  "damp-moisture-condensation": [
    {
      q: "What is the difference between condensation, rising damp and penetrating damp?",
      a: "Condensation is moisture from indoor air settling on cold internal surfaces below dewpoint — the most common cause of mould in modern UK homes. Rising damp is groundwater moving up through masonry by capillary action and is genuinely rare. Penetrating damp is water tracking through the external fabric (failed pointing, blocked airbricks, defective rendering) — far more common than rising damp. Our investigation uses thermal imaging, moisture meters and surface temperature readings to identify which one you actually have before any remedial spec.",
    },
    {
      q: "Why do you measure surface temperature and humidity together?",
      a: "Mould grows on internal surfaces when the surface temperature drops below the dewpoint of the indoor air. By measuring relative humidity, room air temperature and the cold-surface temperature simultaneously, we can calculate the dewpoint margin — a quantified condensation-risk score for that surface. This lets us specify the right intervention (insulation, ventilation, surface temperature improvement) rather than just painting over the symptom.",
    },
    {
      q: "Do you offer a written damp and moisture report?",
      a: "Yes — every damp, mould or moisture investigation is delivered with a written report containing the diagnostic findings, measured values, the probable cause, photographs (including thermal images where relevant), and a prioritised remedial plan. The report is independent and unbiased — we are not selling damp-proofing products.",
    },
    {
      q: "How quickly can you investigate a mould problem?",
      a: "For mould problems where children or vulnerable tenants are affected we prioritise the survey within 7–10 working days. Standard turnaround for the survey and written report is 2–3 weeks from initial enquiry to delivered report.",
    },
  ],
  "soundproofing-acoustics": [
    {
      q: "What can I realistically expect from soundproofing my floor or walls?",
      a: "A well-designed acoustic build-up using mass, absorption and decoupling can deliver a measurable reduction in both airborne noise (voices, TV, music) and impact noise (footsteps, dropped objects). For floors, a four-stage build-up (insulation between joists, resilient bars, acoustic boards, finished deck) typically delivers a 15–25 dB improvement — enough to take audible conversation from clearly intelligible to a low murmur.",
    },
    {
      q: "Why does just adding mass to a floor not work for soundproofing?",
      a: "Sound transmits through structure as well as through air. Simply piling mass on top of a timber floor does not decouple the new layer from the joists below — vibration still travels through the structural connections (flanking transmission). Effective soundproofing requires the mass-spring-mass principle: heavy layers separated by a flexible decoupler (resilient bars, mineral wool or acoustic mounts). That is why our standard acoustic floor build-up uses resilient bars between the joists and the new deck.",
    },
    {
      q: "Do I need building regs approval for soundproofing works?",
      a: "Internal soundproofing of an existing floor or wall in your own dwelling does not usually require Building Regulations approval. If the works are part of a change of use, a conversion (one dwelling becoming two, for example) or a new-build, then Approved Document E applies and a finished sound test will be required. We can specify build-ups that comply with Approved Document E where needed.",
    },
    {
      q: "How much does soundproofing a floor cost?",
      a: "Costs depend on the floor area, the build-up height available and the existing finishes. A typical 4-stage acoustic floor build-up for a single bedroom in a London terrace falls in the £1,800–£3,500 range fully installed and finished — measured against the resulting sound reduction, this is one of the highest-ROI internal works available.",
    },
  ],
  "heat-pump-readiness": [
    {
      q: "What is a heat pump readiness assessment?",
      a: "It is a measured survey that establishes whether your home can run a heat pump efficiently. We carry out a room-by-room heat-loss assessment, review insulation, airtightness and thermal bridging, and check whether your existing radiators or underfloor heating can deliver heat at the low flow temperatures a heat pump needs. You receive an honest readiness verdict and a fabric-first plan to get there.",
    },
    {
      q: "Why do I need a heat-loss survey before installing a heat pump?",
      a: "A heat pump is only efficient at low flow temperatures, and that only works in a home with low heat loss. A measured heat-loss survey sizes the system correctly, identifies the fabric improvements that reduce running cost, and prevents the common outcome of an oversized, expensive heat pump running hot in an under-insulated home.",
    },
    {
      q: "Will I definitely need to insulate before a heat pump?",
      a: "Not always — some homes are already adequate. But many UK homes, especially solid-wall and pre-2000 stock, benefit from targeted fabric improvements first. Our assessment tells you exactly what (if anything) is needed to reach a sensible heat demand, rather than assuming.",
    },
    {
      q: "Are you independent of heat-pump installers?",
      a: "Yes. We do not sell or install heat pumps, so our readiness verdict is about your building, not a product sale. The independent report can be handed to your chosen MCS installer or heat-pump designer.",
    },
  ],
};

// Default SEO defaults applied site-wide (overridden per-page via useSEO).
export const SEO_DEFAULTS = {
  title: "RetrofitIQ | Building Performance Diagnostics & Remediation — London",
  description:
    "Building Performance Diagnostics & Remediation across London & the South East. We investigate, diagnose, design and verify heat loss, damp, air leakage and IAQ.",
};

// Per-route SEO overrides — { title, description } keyed by route slug.
// All copy positions RetrofitIQ as a diagnostics-led building-performance company
// following the Investigate → Diagnose → Design → Remediate → Verify process,
// not a general contractor or single-survey provider.
export const SEO_BY_SLUG = {
  "heat-loss-investigation": {
    title: "Heat Loss Investigation London | RetrofitIQ Building Performance",
    description:
      "Find exactly where your home loses heat — diagnostic FLIR thermal-imaging heat-loss surveys across London & the South East, by a Certified Passive House Designer.",
  },
  "thermal-bridging-survey": {
    title: "Thermal Bridging Survey London | RetrofitIQ Building Performance",
    description:
      "Locate and assess thermal bridges with diagnostic FLIR thermal imaging across London & the South East — cold junctions, lintels, reveals and insulation gaps.",
  },
  "thermal-imaging": {
    title: "Thermal Imaging Diagnostics London | RetrofitIQ Building Performance",
    description:
      "Diagnostic FLIR thermal imaging surveys across London & the South East — heat loss, thermal bridges, missing insulation and condensation risk, expertly assessed.",
  },
  "blower-door-testing": {
    title: "Blower Door & Airtightness Diagnostics London | RetrofitIQ",
    description:
      "Calibrated blower-door air-leakage testing and full diagnostic remediation. We measure ACH₅₀ / m³/h, locate every leak path with smoke + thermal under depressurisation, then design and verify the fix.",
  },
  "damp-moisture-condensation": {
    title: "Damp, Condensation & Mould Diagnostics London | RetrofitIQ",
    description:
      "Independent damp, condensation & mould investigations using measured data — thermal imaging, moisture and dewpoint logging. Treat the cause, not the symptom.",
  },
  "home-health-diagnostics": {
    title: "Home Health Diagnostic Survey London | RetrofitIQ Building Performance",
    description:
      "The complete Building Performance Diagnostic Survey — thermal imaging, blower-door testing, moisture and ventilation diagnostics across London & the South East.",
  },
  "insulation-retrofit": {
    title: "Internal Wall Insulation & Retrofit — Diagnostics + Remediation | RetrofitIQ",
    description:
      "IWI, loft and underfloor insulation specified by a Certified Passive House Designer — we diagnose the moisture risk, design a hygric-safe build-up and verify it.",
  },
  "soundproofing-acoustics": {
    title: "Soundproofing Diagnostics & Acoustic Remediation London | RetrofitIQ",
    description:
      "Measured acoustic diagnostics (Rw / DnT,w / L'nT,w) and designed soundproofing remediation for flats, party walls and floors. Mass, decoupling and absorption — engineered, installed and verified.",
  },
  "external-wall-insulation-cladding": {
    title: "External Wall Insulation Diagnostics & Remediation London | RetrofitIQ",
    description:
      "EWI, ventilated façade and brick-slip cladding systems — fully engineered Building Performance retrofits with continuous insulation, thermal-bridge correction and a verified outcome.",
  },
  "mvhr-ventilation": {
    title: "MVHR & Ventilation Diagnostics + Design London | RetrofitIQ",
    description:
      "MVHR design, airflow calculation, commissioning and IAQ verification — followed up with measured CO₂ and humidity data so we know your ventilation actually works.",
  },
  "indoor-air-quality": {
    title: "Indoor Air Quality Diagnostics London | RetrofitIQ",
    description:
      "Measured IAQ assessments — CO₂, humidity, PM2.5, VOCs, formaldehyde — followed by source-control, ventilation and filtration remediation, then re-measured for verified results.",
  },
  "heat-pump-readiness": {
    title: "Heat Pump Readiness Assessment London | RetrofitIQ Heat Loss Survey",
    description:
      "Is your home heat-pump ready? Measured heat-loss, fabric, airtightness & emitter assessment across London & the South East by a Passive House Designer.",
  },
};

export const HERO_BANNER = "/hero-blower-door.jpg";

// Certified Passive House Designer badge (official seal).
export const PASSIVE_HOUSE_BADGE = {
  src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/n1hm2tyd_seal_en.png",
  alt: "Certified Passive House Designer — official seal awarded to George Sora by the Passive House Institute",
  href: "https://passivehouse.com/",
  label: "Certified Passive House Designer",
};

export const PROBLEMS = [
  { title: "Heat Loss", desc: "Excessive thermal losses through walls, roofs and junctions." },
  { title: "Cold Walls", desc: "Surfaces below dewpoint that feel cold and harbour condensation." },
  { title: "Condensation", desc: "Surface and interstitial moisture problems." },
  { title: "Damp Problems", desc: "Rising, penetrating and construction-related damp." },
  { title: "Mould Growth", desc: "Microbial growth from sustained surface humidity." },
  { title: "Draughts", desc: "Uncontrolled air movement and cold spots." },
  { title: "Air Leakage", desc: "Unintended air paths reducing comfort and efficiency." },
  { title: "Poor Ventilation", desc: "Stale air, CO₂ build-up and humidity retention." },
  { title: "High Energy Bills", desc: "Inefficient envelopes and uncontrolled losses." },
  { title: "Thermal Bridges", desc: "Localised heat-loss paths through structure." },
  { title: "Noise Transmission", desc: "Airborne and impact noise through floors, walls, ceilings." },
  { title: "Overheating", desc: "Summer overheating from solar gains and poor ventilation." },
  { title: "Poor Indoor Air Quality", desc: "Pollutants, VOCs and inadequate fresh-air supply." },
];

export const METHODS = [
  { title: "FLIR Thermal Camera", desc: "Infrared surveys to map heat-loss patterns, missing insulation and thermal bridges." },
  { title: "Blower Door Testing System", desc: "Pressure-tested airtightness measurement to quantify infiltration at 50 Pa." },
  { title: "Moisture Meter", desc: "Surface and deep moisture readings across substrates and finishes." },
  { title: "Smoke Tracing Equipment", desc: "Visualising air-leakage paths and ventilation behaviour in real time." },
  { title: "Humidity & Dewpoint Logger", desc: "Relative humidity and dewpoint logging to assess condensation risk." },
  { title: "Surface Temperature Probe", desc: "Identifying dewpoint and cold-bridge conditions on internal surfaces." },
  { title: "Ventilation Assessment Equipment", desc: "Anemometers and balometers to verify extract, supply and MVHR airflows." },
  { title: "Building Performance Assessments", desc: "Holistic reporting on fabric, services and indoor environment." },
];

export const PROCESS = [
  { step: "01", title: "Investigate", desc: "Site visit, visual inspection, listening to your concerns and capturing the full picture of how the building is performing." },
  { step: "02", title: "Diagnose", desc: "Thermal imaging, blower door testing, moisture, humidity, ventilation and acoustics — measured data to identify the root cause, not just the symptom." },
  { step: "03", title: "Design", desc: "Building-physics-led specification of the right remediation: insulation, airtightness, ventilation, soundproofing or envelope upgrades." },
  { step: "04", title: "Remediate", desc: "Carry out the works correctly using appropriate materials, detailing and Passive House principles — one accountable team from start to finish." },
  { step: "05", title: "Verify", desc: "Re-test, re-measure and document the improvement so the result is provable, not assumed." },
];

export const SERVICE_GROUPS = [
  {
    slug: "diagnostics",
    title: "Diagnostics",
    label: "01 / Diagnostics",
    summary:
      "Building diagnostics, thermal imaging, blower door testing and forensic investigations to identify the root cause of damp, heat loss, condensation and ventilation problems.",
    items: [
      "Home Health Diagnostic Surveys",
      "Blower Door Testing",
      "Thermal Imaging Surveys",
      "Moisture Investigations",
      "Damp Investigations",
      "Condensation Assessments",
      "Mould Investigations",
      "Heat Loss Analysis",
      "Ventilation Assessments",
      "Acoustic Assessments",
      "Building Performance Reports",
    ],
  },
  {
    slug: "insulation-retrofit",
    title: "Insulation & Retrofit",
    label: "02 / Insulation & Retrofit",
    summary:
      "Targeted insulation and retrofit upgrades based on measured data — designed using Passive House principles to deliver real, verified performance.",
    items: [
      "Internal Wall Insulation",
      "Loft Insulation",
      "Floor Insulation",
      "Roof & Ceiling Insulation",
      "Thermal Bridge Reduction",
      "Airtightness Improvements",
      "Retrofit Planning",
      "Building Performance Upgrades",
    ],
  },
  {
    slug: "external-envelope-cladding",
    title: "External Envelope & Cladding",
    label: "03 / External Envelope & Cladding",
    summary:
      "Specialist external wall insulation, ventilated façade and cladding systems for long-term durability, thermal performance and aesthetic upgrades.",
    items: [
      "External Wall Insulation",
      "Ventilated Façade Systems",
      "Cladding Systems",
      "Building Envelope Improvements",
      "Weatherproofing Solutions",
      "Thermal Performance Upgrades",
    ],
  },
  {
    slug: "ventilation-mvhr",
    title: "Ventilation & MVHR",
    label: "04 / Ventilation & MVHR",
    summary:
      "Mechanical ventilation with heat recovery (MVHR) design advice and ventilation strategies that protect indoor air quality and building fabric.",
    items: [
      "Ventilation Strategy Advice",
      "MVHR Design Advice",
      "Airflow Calculations",
      "Duct Route Planning",
      "Equipment Recommendations",
      "Indoor Air Quality Improvements",
      "Heat Recovery Ventilation Solutions",
    ],
  },
  {
    slug: "soundproofing-acoustics",
    title: "Soundproofing & Acoustics",
    label: "05 / Soundproofing & Acoustics",
    summary:
      "Acoustic surveys and full soundproofing systems for airborne and impact noise across walls, floors and ceilings — assessed, designed and verified.",
    items: [
      "Soundproofing Assessments",
      "Acoustic Surveys",
      "Impact Noise Reduction",
      "Airborne Noise Reduction",
      "Acoustic Floor Systems",
      "Acoustic Wall Systems",
      "Acoustic Ceiling Systems",
      "Full Room Acoustic Upgrades",
      "Residential Soundproofing Solutions",
    ],
  },
];

export const PASSIVE_HOUSE_PRINCIPLES = [
  { title: "Superinsulation", desc: "High-performance insulation across walls, roofs and floors to minimise heat loss." },
  { title: "Airtight Construction", desc: "Controlled airtightness, measured and verified by blower door testing." },
  { title: "Thermal Bridge Reduction", desc: "Continuous insulation detailing to eliminate localised heat-loss paths." },
  { title: "High-Performance Windows & Doors", desc: "Triple-glazed and insulated frames to balance comfort and daylight." },
  { title: "Mechanical Ventilation with Heat Recovery", desc: "Constant fresh air with up to 90% heat recovery." },
  { title: "Measured & Verified Performance", desc: "Quantified results — not assumptions — through testing and monitoring." },
];

export const SERVICE_PAGES = {
  "heat-loss-investigation": {
    title: "Heat Loss Investigation",
    overline: "Diagnostics",
    intro:
      "A focused investigation into where, how and why your home is losing heat — combining FLIR thermal imaging, surface-temperature mapping and fabric analysis to pinpoint the real sources of heat loss before any money is spent on remediation.",
    sections: [
      {
        heading: "What we investigate",
        bullets: [
          "Continuous cold surfaces across walls, floors and roofs losing heat to outside",
          "Poorly insulated doors, glazing and reveals acting as heat-loss hotspots",
          "External-wall sections and junctions underperforming against the rest of the envelope",
          "Cold bridging at wall-to-ceiling and wall-to-floor junctions",
          "Draught-driven heat loss around openings and service penetrations",
        ],
      },
      {
        heading: "What you receive",
        bullets: [
          "Annotated thermal images locating each heat-loss area",
          "Surface-temperature comparison between affected and sound areas",
          "A prioritised list of where heat loss is costing you the most",
          "A building-physics-led remediation plan — fabric first, no guesswork",
        ],
      },
    ],
  },
  "thermal-bridging-survey": {
    title: "Thermal Bridging Survey",
    overline: "Diagnostics",
    intro:
      "A specialist survey to locate and assess thermal bridges — the localised cold paths where the building structure bypasses the insulation layer at junctions, lintels, reveals and slab edges, driving heat loss and condensation risk.",
    sections: [
      {
        heading: "What we identify",
        bullets: [
          "Geometric thermal bridges at corners and wall-to-ceiling / wall-to-floor junctions",
          "Repeating bridges through structural elements (lintels, beams, balconies)",
          "Insulation discontinuity behind dot-and-dab plasterboard",
          "Cold reveals and window/door surrounds bypassing the insulation line",
          "Junctions most at risk of surface condensation and black mould",
        ],
      },
      {
        heading: "What you receive",
        bullets: [
          "Annotated thermal images locating each thermal bridge",
          "Assessment of condensation and mould risk at each junction",
          "Guidance on whether the bridge is geometric, repeating or workmanship-related",
          "A targeted remediation strategy to restore insulation continuity",
        ],
      },
    ],
  },
  "home-health-diagnostics": {
    title: "Home Health Diagnostic Survey",
    overline: "Diagnostics",
    intro:
      "A comprehensive whole-home diagnostic assessment combining visual inspection, thermal imaging, moisture, humidity and airflow measurements to identify the root cause of building problems.",
    sections: [
      {
        heading: "What's included",
        bullets: [
          "On-site visual inspection of the property and reported issues",
          "Thermal imaging survey of suspect areas (walls, ceilings, junctions, windows)",
          "Moisture readings on affected substrates",
          "Indoor relative humidity and surface temperature analysis",
          "Smoke tracing of suspected air-leakage paths (where required)",
          "Optional blower door test for whole-house airtightness",
          "Written diagnostic report with findings, photographs and recommendations",
        ],
      },
      {
        heading: "Who it's for",
        bullets: [
          "Homeowners experiencing damp, mould, cold or high bills",
          "Landlords and housing associations investigating tenant complaints",
          "Architects and developers commissioning pre-purchase or pre-retrofit surveys",
          "Property managers needing evidence-based investigation reports",
        ],
      },
    ],
  },
  "indoor-air-quality": {
    title: "Indoor Air Quality Assessments",
    overline: "Indoor Environment",
    intro:
      "Measured assessments of indoor air quality, humidity, ventilation rates and pollutant build-up — protecting occupant health and the building fabric.",
    sections: [
      {
        heading: "What we measure",
        bullets: [
          "Carbon dioxide (CO₂) build-up as a proxy for ventilation effectiveness",
          "Relative humidity, dewpoint and condensation risk",
          "Extract and supply airflow rates per room",
          "Particulate matter (PM2.5 / PM10) where required",
          "Volatile organic compound (VOC) screening",
          "Mould risk areas via surface temperature and moisture mapping",
        ],
      },
      {
        heading: "Why it matters",
        bullets: [
          "Modern, airtight homes need controlled ventilation to stay healthy",
          "Poor IAQ contributes to asthma, allergies and sleep disturbance",
          "Persistent high humidity drives condensation, mould and fabric decay",
          "Targeted, measured solutions outperform off-the-shelf fans",
        ],
      },
    ],
  },
  "thermal-imaging": {
    title: "Thermal Imaging Surveys",
    overline: "Diagnostics",
    intro:
      "Professional infrared thermography using FLIR equipment to visualise heat-loss, missing insulation, thermal bridges, air leakage and hidden moisture.",
    sections: [
      {
        heading: "What we look for",
        bullets: [
          "Missing or compressed insulation in walls, lofts and floors",
          "Thermal bridges at junctions, lintels and reveals",
          "Cold walls vulnerable to condensation and mould",
          "Air-leakage signatures around windows, doors and service penetrations",
          "Underfloor heating circuits, leaks and hidden moisture",
        ],
      },
      {
        heading: "Survey conditions",
        bullets: [
          "Carried out with sufficient temperature differential between inside and outside",
          "Combined with surface temperature, humidity and dewpoint analysis",
          "Calibrated, measured imagery — not 'pretty pictures'",
        ],
      },
    ],
  },
  "blower-door-testing": {
    title: "Blower Door Testing",
    overline: "Diagnostics",
    intro:
      "Standardised airtightness testing using a calibrated blower door fan to quantify how much air a building leaks at a reference pressure of 50 Pascals.",
    sections: [
      {
        heading: "Where it's used",
        bullets: [
          "New-build airtightness verification",
          "Passive House and EnerPHit certification testing",
          "Pre-retrofit baseline measurement",
          "Post-retrofit verification of airtightness improvements",
          "Diagnosing draughts, cold spots and ventilation imbalance",
        ],
      },
      {
        heading: "What you receive",
        bullets: [
          "Measured air-change rate (ACH₅₀ or m³/h·m² at 50 Pa)",
          "Smoke tracing to locate dominant leakage paths",
          "Targeted recommendations to reduce air leakage",
          "Optional re-test to verify improvements",
        ],
      },
    ],
  },
  "damp-moisture-condensation": {
    title: "Damp, Moisture & Condensation",
    overline: "Diagnostics",
    intro:
      "Forensic investigation of damp, condensation and mould problems. We measure before we recommend — no guesswork, no over-treatment.",
    sections: [
      {
        heading: "Common findings",
        bullets: [
          "Surface condensation from cold bridging and poor ventilation",
          "Interstitial condensation within wall build-ups",
          "Penetrating damp through cracked render, pointing or roofs",
          "Rising damp confirmed by deep moisture readings (rare in practice)",
          "Plumbing leaks misdiagnosed as 'rising damp'",
        ],
      },
      {
        heading: "Our process",
        bullets: [
          "Visual inspection and client interview",
          "Surface and deep moisture mapping",
          "Humidity & dewpoint logging where appropriate",
          "Thermal imaging to confirm cold surfaces",
          "Written report with diagnosis and prioritised remedial options",
        ],
      },
    ],
  },
  "soundproofing-acoustics": {
    title: "Soundproofing & Acoustics",
    overline: "Acoustics",
    intro:
      "Acoustic assessments and full soundproofing solutions for residential properties — addressing both airborne noise (voices, TV, traffic) and impact noise (footsteps, vibrations).",
    sections: [
      {
        heading: "Systems we install",
        bullets: [
          "Independent acoustic wall systems",
          "Acoustic ceiling systems with resilient hangers",
          "Floating floors and resilient acoustic underlays",
          "Full-room treatments combining mass, decoupling, absorption and damping",
          "Targeted treatments for party walls and HMO conversions",
        ],
      },
      {
        heading: "How we design",
        bullets: [
          "Acoustic assessment of the existing build-up",
          "Identification of flanking paths (often the real culprit)",
          "Specification based on mass-spring-mass and decoupling principles",
          "Installation with verified detailing — no shortcuts",
        ],
      },
    ],
  },
  "insulation-retrofit": {
    title: "Insulation & Retrofit",
    overline: "Performance",
    intro:
      "Targeted, measured retrofit and insulation upgrades — designed using Passive House principles and verified with testing where required.",
    sections: [
      {
        heading: "Scope of works",
        bullets: [
          "Internal Wall Insulation (IWI) with hygrothermal-aware build-ups",
          "Loft, roof and ceiling insulation upgrades",
          "Suspended timber and solid floor insulation",
          "Thermal bridge mitigation at junctions and reveals",
          "Whole-house airtightness improvements",
        ],
      },
      {
        heading: "Why our approach is different",
        bullets: [
          "Diagnosis-led — we measure before recommending",
          "Hygrothermally safe build-ups, not 'one size fits all'",
          "Detailing focused on junctions, the real source of failure",
          "Optional post-works verification through testing",
        ],
      },
    ],
  },
  "external-wall-insulation-cladding": {
    title: "External Wall Insulation & Cladding",
    overline: "Building Envelope",
    intro:
      "External wall insulation (EWI), ventilated façades and cladding systems delivered by a specialist with 10+ years of envelope-systems experience.",
    sections: [
      {
        heading: "Systems",
        bullets: [
          "Render-finished External Wall Insulation (EWI)",
          "Ventilated façade systems with rainscreen cladding",
          "Brick-slip, fibre-cement, timber and composite cladding",
          "Insulated render and silicone top-coat finishes",
          "Detailing for windows, reveals, sills, eaves and verges",
        ],
      },
      {
        heading: "Why ventilated façades?",
        bullets: [
          "Drained, drying cavity behind the cladding",
          "Improved long-term durability of the substrate",
          "Easy panel replacement and serviceability",
          "Continuous external insulation reduces thermal bridging",
        ],
      },
    ],
  },
  "mvhr-ventilation": {
    title: "MVHR & Ventilation",
    overline: "Indoor Environment",
    intro:
      "Mechanical Ventilation with Heat Recovery (MVHR) design advice and ventilation strategies to deliver constant fresh air with minimal heat loss.",
    sections: [
      {
        heading: "Services",
        bullets: [
          "Ventilation strategy advice for retrofit and new-build",
          "MVHR system sizing and design advice",
          "Airflow calculations per room and overall",
          "Duct route planning to minimise pressure loss and noise",
          "Equipment recommendations from reputable manufacturers",
          "Commissioning and airflow verification",
        ],
      },
      {
        heading: "Why MVHR matters",
        bullets: [
          "Constant supply of filtered fresh air",
          "Up to 90% heat recovery from extract air",
          "Lower humidity and condensation risk",
          "Better indoor air quality, especially in airtight homes",
        ],
      },
    ],
  },
  "heat-pump-readiness": {
    title: "Heat Pump Readiness Assessment",
    overline: "Diagnostics · Heat Pump Readiness",
    intro:
      "Before you install a heat pump, find out whether your home can actually run one efficiently. A measured heat-loss survey, fabric and airtightness assessment and emitter review — so a low-temperature heat pump performs as promised, instead of running hot, expensive and disappointing.",
    sections: [
      {
        heading: "What we assess",
        bullets: [
          "Whole-house heat loss (room-by-room) using a measured fabric survey, not a desktop estimate",
          "Wall, roof and floor insulation levels and the realistic upgrades available",
          "Airtightness and uncontrolled air leakage that drives up the heat demand",
          "Thermal bridges and cold surfaces that undermine low-temperature heating",
          "Existing emitters (radiators / underfloor) and whether they can deliver heat at a low flow temperature",
          "Ventilation and moisture behaviour as the fabric is tightened",
        ],
      },
      {
        heading: "What you receive",
        bullets: [
          "A measured heat-loss figure and an honest readiness verdict — ready now, ready after specific works, or not yet viable",
          "A prioritised, fabric-first improvement plan to reach the heat demand a heat pump needs",
          "Guidance on the flow temperature your home could realistically run at",
          "Emitter and distribution recommendations to support a low-temperature system",
          "An independent report you can hand to your heat-pump installer or MCS designer",
        ],
      },
      {
        heading: "Why fabric first, heat pump second",
        bullets: [
          "Heat pumps are most efficient at low flow temperatures — which only works in a low-heat-loss home",
          "Reducing heat loss before sizing the heat pump means a smaller, cheaper, quieter unit",
          "Existing radiators often only work at high flow temperatures unless the fabric is improved",
          "Airtightness and insulation improvements cut running cost whatever heating you choose",
          "We are independent of installers — our verdict is about your building, not a sale",
        ],
      },
    ],
  },
};

// Real, verified case studies built ONLY from real customer reviews + project notes.
// No invented details. New entries are added as new reviews + project photos arrive.
export const FEATURED_CASE_STUDY = {
  slug: "francis-luton-blower-door-thermal-imaging",
  badge: "Featured Investigation",
  source: "MyBuilder",
  sourceUrl: "https://www.mybuilder.com/profile/g-a-s-general-building-services-ltd",
  customer: "Francis",
  location: "Luton",
  date: "15 Dec 2025",
  jobTitle: "Loft & Kitchen Insulation / Draught-Proofing — Diagnostic Survey",
  problem:
    "The customer was looking for a proper diagnostic assessment of their property (loft and kitchen) to understand where air leakage and heat loss were actually occurring, before committing to any insulation or draught-proofing works.",
  method:
    "On-site Home Health Diagnostic Survey combining a blower door test, smoke testing and a thermal imaging survey.",
  findings:
    "The thermal images and smoke tests showed exactly where air leakage and heat loss were occurring. George explained the building physics behind each issue so the customer understood the causes, the risks they create and how they relate to each other.",
  solution:
    "A detailed, well-structured written report was delivered, with clear explanations of the causes, risks and recommended remedial options. The recommendations were honest and practical about priorities, costs and what would (and would not) make a real difference.",
  outcome:
    "The customer received a proper diagnostic assessment — not a superficial inspection. They were extremely impressed with the professionalism and depth of knowledge and would have no hesitation in recommending the service.",
  methods: ["Blower Door Testing", "Smoke Tracing", "Thermal Imaging Survey"],
  reviewQuote:
    "George carried out a blower door test, smoke testing and thermal imaging survey at our property, and we were extremely impressed with both his professionalism and his depth of knowledge. He took the time to explain everything clearly, using the thermal images and smoke tests to show exactly where air leakage and heat loss were occurring. The final report was very detailed, well-structured and easy to understand, with clear explanations of the causes, risks and recommended remedial options. What we particularly appreciated was that George didn't just point out problems, he explained the building physics behind them and was honest and practical about priorities, costs and what would (and wouldn't) make a real difference. He was punctual, tidy, respectful of our home and very easy to communicate with throughout the process. We would have no hesitation in recommending him to anyone looking for a proper diagnostic assessment rather than a superficial inspection. Thank you again, George",
};
export const CASE_STUDIES = [
  {
    slug: "n17-soundproofing-multi-room-acoustic-upgrade",
    title: "Multi-Room Acoustic Floor Build-Up + Wall Upgrade — N17",
    customer: "Verified Checkatrade customer",
    location: "N17, London",
    date: "June 2026",
    source: "Checkatrade",
    sourceUrl: "https://www.checkatrade.com/trades/gasgeneralbuildingservicesltd",
    tags: ["Soundproofing", "RW5 Acoustic Mineral Wool", "Resilient Bars", "Acoustic Floor", "Wall Upgrade"],
    problem:
      "The customer was experiencing significant airborne and impact noise transmission through the existing timber floors across multiple rooms — voices, music and footsteps from rooms above and adjacent were clearly audible, affecting day-to-day comfort. One bedroom in particular needed both floor AND wall acoustic upgrades.",
    method:
      "On-site assessment of the existing floor and wall build-ups in every affected room, followed by a four-stage acoustic floor upgrade per room (lift existing finishes → mineral wool between joists → resilient acoustic bars → new floorboards → acoustic underlay → new carpet) plus a designed acoustic wall upgrade to the most-affected bedroom.",
    findings:
      "The existing floors offered no acoustic insulation between joists and no decoupling between the upper finish and the joists. Sound was transmitting directly through the structure as both airborne and impact noise. The most-affected bedroom additionally had a direct flanking path through the party wall requiring an independent acoustic wall layer.",
    solution:
      "Per room — Stage 1: existing floor finishes removed. Stage 2: RW5 acoustic mineral wool installed between joists. Stage 3: resilient acoustic bars fixed across the joists to decouple the new deck. Stage 4: new floorboards laid, acoustic underlay over, new carpet finished. Additional wall upgrade in one bedroom using independent stud + mineral wool + acoustic plasterboard to break the flanking path.",
    outcome:
      "Measurably quieter rooms across the home with significant reductions in both airborne noise (voices, TV) and impact noise (footsteps). Customer rated the service 10/10 on Checkatrade and confirmed they will be using RetrofitIQ again for further works.",
    reviewQuote:
      "Excellent Service. Highly recommended. The work was done to perfection. We were always kept informed of what was happening and given advice of the best cost effective option. Anyone who choses this company will not be disappointed. They are extremely professional and good value for money. We will be using them again to do some more work. Thank you GAS.",
    galleryKey: "n17",
    cover: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/su2kdm3s_20260526_191011.jpg",
    serviceTag: "Soundproofing & Acoustics",
    relatedServices: ["soundproofing-acoustics", "insulation-retrofit"],
  },
  {
    slug: FEATURED_CASE_STUDY.slug,
    title: FEATURED_CASE_STUDY.jobTitle,
    location: FEATURED_CASE_STUDY.location,
    customer: FEATURED_CASE_STUDY.customer,
    date: FEATURED_CASE_STUDY.date,
    source: FEATURED_CASE_STUDY.source,
    sourceUrl: FEATURED_CASE_STUDY.sourceUrl,
    tags: FEATURED_CASE_STUDY.methods,
    problem: FEATURED_CASE_STUDY.problem,
    method: FEATURED_CASE_STUDY.method,
    findings: FEATURED_CASE_STUDY.findings,
    solution: FEATURED_CASE_STUDY.solution,
    outcome: FEATURED_CASE_STUDY.outcome,
    reviewQuote: FEATURED_CASE_STUDY.reviewQuote,
    galleryKey: "francis",
    cover: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/opgn0tlk_20251212_155711.jpg",
    serviceTag: "Home Health Diagnostics",
    relatedServices: ["home-health-diagnostics", "blower-door-testing", "thermal-imaging", "damp-moisture-condensation"],
  },
  {
    slug: "luke-large-underfloor-insulation-soundproofing",
    title: "Underfloor Insulation & Soundproofing — Lifted Floor, Insulation Between Joists, New Acoustic Deck",
    customer: "Luke Large",
    location: "E17, London",
    date: "April 2026",
    source: "Checkatrade",
    sourceUrl: "https://www.checkatrade.com/trades/gasgeneralbuildingservicesltd",
    tags: ["Underfloor Insulation", "Soundproofing", "Acoustic Floor", "Resilient Bars"],
    problem:
      "The customer needed the existing finished floor lifted so that proper insulation could be installed between the joists — to reduce heat loss into the cold void below and improve airborne and impact noise transmission from the room below.",
    method:
      "On-site assessment of the existing suspended-timber floor and the void beneath, followed by a four-stage underfloor build-up: lift existing floor → install mineral wool insulation between joists → fix resilient acoustic bars across the joists → lay new moisture-resistant acoustic boards (RW5 system) as the new deck.",
    findings:
      "The existing floor build-up offered virtually no thermal or acoustic insulation: heat was being lost downward into the void, and sound was transmitting directly through the joists as both airborne and impact noise. The joists themselves were sound and reusable, so the optimal solution was an insulated, decoupled new deck rather than a full replacement.",
    solution:
      "Lifted the existing floor and exposed the joists; installed mineral wool insulation between the joists (thermal + acoustic); fixed resilient acoustic bars across the joists to decouple the new deck from the structure; finished with moisture-resistant acoustic boards (RW5 system) ready for the floor finish. Every stage was photographed for the client's records.",
    outcome:
      "A warmer, measurably quieter room with a clean, fully-documented build-up. The client confirmed they would happily reuse RetrofitIQ for similar works or general construction — verified in his Checkatrade review.",
    reviewQuote:
      "George was brilliant. We required the existing floor to be lifted and insulation installed. Throughout the process George was tidy, considerate and offered advice on how to achieve the best outcome. The team documented the process throughly with photos for my records. I would definitely use GAS again for similar works or general construction in our home.",
    galleryKey: "luke",
    cover: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/9e66o2jw_20260324_093148.jpg",
    serviceTag: "Underfloor Insulation & Soundproofing",
    relatedServices: ["soundproofing-acoustics", "insulation-retrofit"],
  },
  {
    slug: "giuliano-internal-wall-insulation-iwi-celotex",
    title: "Internal Wall Insulation (IWI) — Cold Cupboard, Damp & Mould Remediation",
    customer: "Giuliano",
    location: "Shepherds Bush, London",
    date: "February 2026",
    source: "MyBuilder",
    sourceUrl: "https://www.mybuilder.com/profile/g-a-s-general-building-services-ltd",
    tags: ["Internal Wall Insulation", "Celotex / Kingspan", "Damp & Mould", "Thermal Imaging"],
    problem:
      "A built-in cupboard against an external wall was generating persistent damp and mould — the cold internal surface was sitting below dewpoint on cold days, condensing moisture out of the air and causing the tenant ongoing problems. The property is tenanted, so works needed to be coordinated directly with the tenant around their availability.",
    method:
      "Two pre-works site visits to understand the scope properly (no blind phone quoting), combined with FLIR thermal imaging to confirm the cold surfaces and identify the worst-affected areas. The work was then specified as a full internal wall insulation (IWI) build-up using Celotex / Kingspan-equivalent rigid PIR boards — a hygrothermally aware solution sized to bring the internal surface temperature well above dewpoint.",
    findings:
      "The thermal images confirmed pronounced cold spots and cold bands on the cupboard wall, lintel and reveals — surfaces between 15.7 °C and 17.7 °C against room air of ~21 °C, which is a textbook condensation/mould risk. Two site visits before quoting also revealed the job was trickier than a single day allowed for, so the scope was honestly re-priced to two days rather than rushed.",
    solution:
      "Full IWI installation to the cold cupboard: rigid PIR insulation boards (Celotex / Kingspan equivalent) fixed to the cold wall, with airtight detailing at junctions to control vapour movement. Access to the tenanted property was arranged directly with the tenant, working around their availability. Project completed under a 1-year workmanship warranty.",
    outcome:
      "A dry, warmer cupboard with the internal surface now well above dewpoint — eliminating the damp and mould source at the root. The tenant is happy with the solution, the landlord has a 1-year warranty, and the job is signed off via the verbatim MyBuilder review.",
    reviewQuote:
      "The job was completed to am excellent standard. It exceeded my expectations. The property is tenanted and the process was smooth as he got on touch and arranged access with the tenant independently, working around the availability. It was trickier that expected so the price went up a bit from the original quote but it requires two days of work instead of one. He attended the property twice even before doing the work to understand the scope of work (didn't quote blindly over the phone). The job carried out is a full insulation of a built-in cupboard which before creates damp and mould, tenant is very happy with the solution. Comes with a 1year warranty. Extremely polite, reliable and clean.",
    galleryKey: "giuliano",
    cover: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/s0q98upa_FLIR0606.jpg",
    serviceTag: "Internal Wall Insulation (IWI)",
    relatedServices: ["insulation-retrofit", "thermal-imaging", "damp-moisture-condensation"],
  },
  {
    slug: "property-assessment-costed-improvement-report",
    title: "Property Assessment & Costed Improvement Report — Whole-Flat Diagnostic Survey",
    customer: "Verified Checkatrade customer",
    location: "IG8, London",
    date: "March 2026",
    source: "Checkatrade",
    sourceUrl: "https://www.checkatrade.com/trades/gasgeneralbuildingservicesltd",
    tags: ["Property Assessment", "Building Diagnostics", "Thermal Imaging", "Costed Improvements", "Building Performance Strategy"],
    problem:
      "The customer needed a structured, evidence-based assessment of their whole flat — not a tick-box survey — to understand exactly where heat, comfort and indoor-environment problems were originating, and to receive a fully-costed schedule of recommended improvements they could prioritise and budget against.",
    method:
      "On-site Home Health Diagnostic Survey of the flat combining: visual inspection room by room, FLIR thermal imaging of all critical surfaces (walls, ceilings, lintels, reveals, floor junctions, service penetrations), surface temperature and humidity readings, defect identification against building-performance criteria, and conversion of the findings into a costed improvement schedule.",
    findings:
      "Multiple thermal defects identified and measured across the flat: localised thermal bridges at wall/ceiling corners (cold spots down to 19.1 °C against room baseline ~21 °C), pronounced cold bands along door-head lintels (16.8 °C → 29.4 °C across the same image), a cooler perimeter band at the wall/floor junction (evidence of a heat-loss path through the slab edge), and uniformly cool internal wall surfaces giving an accurate baseline for the costed-improvement comparison.",
    solution:
      "A thorough written diagnostic report was delivered covering each defect, the building-science reason for it, and a fully-costed schedule of proposed improvements ranked by impact and cost. The recommendations covered thermal-bridge mitigation, perimeter detailing, surface-temperature improvement and a wider Building Performance Strategy the client can implement in stages.",
    outcome:
      "The customer received a proper, evidence-based assessment with full costings for every proposed improvement — exactly what they were looking for. They left a 5-star Checkatrade review describing the report as 'very thorough' with full costings for all proposed improvements, and a glowing recommendation.",
    reviewQuote:
      "George was friendly, arrived on time and was very helpful during the assessment. He answered all of my questions and showed a passion for what he does. The report provided was very thorough and George was able to provide full costings for all proposed improvements to my flat. I highly recommend him!",
    galleryKey: "property",
    cover: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/b2nlithx_FLIR1000.jpg",
    serviceTag: "Property Assessment & Costed Report",
    relatedServices: ["home-health-diagnostics", "thermal-imaging"],
  },
];

export const REVIEWS = [
  // ---------- Checkatrade (verified, scored /10) ----------
  {
    name: "Verified Checkatrade customer",
    source: "Checkatrade",
    location: "N17, London",
    job: "Soundproofing — acoustic floor build-up across multiple rooms + wall upgrade",
    rating: 5,
    date: "Posted June 2026",
    quote:
      "Excellent Service. Highly recommended. The work was done to perfection. We were always kept informed of what was happening and given advice of the best cost effective option. Anyone who choses this company will not be disappointed. They are extremely professional and good value for money. We will be using them again to do some more work. Thank you GAS.",
    summary:
      "Whole-floor acoustic upgrade across multiple rooms in an N17 home — existing finishes stripped, RW5 acoustic mineral wool installed between joists, decoupling treatment applied to the floor structure, new floorboards laid, acoustic underlay + new carpet finished. One bedroom received an additional acoustic wall upgrade. Outcome: a measurably quieter home and a 10/10 review.",
  },
  {
    name: "Luke Large",
    source: "Checkatrade",
    location: "E17, London",
    job: "Underfloor insulation",
    rating: 5,
    date: "Posted 05 April",
    quote:
      "George was brilliant. We required the existing floor to be lifted and insulation installed. Throughout the process George was tidy, considerate and offered advice on how to achieve the best outcome. The team documented the process throughly with photos for my records. I would definitely use GAS again for similar works or general construction in our home.",
    summary:
      "Suspended-timber underfloor insulation upgrade — lifted finished floor, installed continuous insulation between joists, fully documented with photographs. Outcome: warmer floors, reduced heat loss and a clean, well-documented retrofit the client is happy to reuse.",
  },
  {
    name: "Verified Checkatrade customer",
    source: "Checkatrade",
    location: "IG8, London",
    job: "Property assessment & costed improvements report",
    rating: 5,
    date: "Posted 13 March",
    quote:
      "George was friendly, arrived on time and was very helpful during the assessment. He answered all of my questions and showed a passion for what he does. The report provided was very thorough and George was able to provide full costings for all proposed improvements to my flat. I highly recommend him!",
    summary:
      "Whole-flat Home Health Diagnostic assessment with a thorough written report and a fully-costed schedule of improvement options. Outcome: clear evidence-based plan the client can prioritise and budget against.",
  },
  {
    name: "Verified Checkatrade customer",
    source: "Checkatrade",
    location: "SG6",
    job: "Multi-trade renovation works (occupied property)",
    rating: 5,
    date: "Posted 06 February",
    quote:
      "Very professional, communicated clearly throughout the project, and paid close attention to detail. We were moving in the middle of the works, while also working from home, and George made sure to always work around us, prioritise things that would help us and keep things tidy in the house. Very knowledgeable and friendly, already booked for more projects!",
    summary:
      "Sensitive multi-trade renovation programme delivered while the client was moving in and working from home. Outcome: minimal disruption, tidy site, and a relationship strong enough that the client booked further works.",
  },

  // ---------- MyBuilder (most recent 8, older 2 excluded as requested) ----------
  {
    name: "MyBuilder user from Orpington",
    source: "MyBuilder",
    location: "Orpington",
    job: "Investigation of moisture / paint swelling around internal window",
    rating: 5,
    date: "17 Feb 2026",
    quote: null,
    summary:
      "Diagnostic investigation of moisture and paint swelling around an internal window reveal. Surface moisture, dewpoint and thermal imaging used to identify the source (cold reveal + interstitial condensation) and recommend a remediation plan.",
  },
  {
    name: "MyBuilder user from Greenwich, London",
    source: "MyBuilder",
    location: "Greenwich, London",
    job: "Replace insulated ventilation duct hose in loft",
    rating: 5,
    date: "10 Feb 2026",
    quote: null,
    summary:
      "Replaced a damaged insulated ventilation duct hose in the loft, restoring efficient warm-air movement, preventing condensation inside the duct and protecting the loft insulation from moisture damage.",
  },
  {
    name: "Giuliano",
    source: "MyBuilder",
    location: "Shepherds Bush, London",
    job: "Insulation of internal wall with Celotex or equivalent",
    rating: 5,
    date: "1 Feb 2026",
    quote:
      "The job was completed to am excellent standard. It exceeded my expectations. The property is tenanted and the process was smooth as he got on touch and arranged access with the tenant independently, working around the availability. It was trickier that expected so the price went up a bit from the original quote but it requires two days of work instead of one. He attended the property twice even before doing the work to understand the scope of work (didn't quote blindly over the phone). The job carried out is a full insulation of a built-in cupboard which before creates damp and mould, tenant is very happy with the solution. Comes with a 1year warranty. Extremely polite, reliable and clean.",
    summary:
      "Internal Wall Insulation (IWI) with Celotex/Kingspan-equivalent boards to a cold built-in cupboard that was generating damp and mould. Scope confirmed by two pre-works site visits — no blind phone quoting. Outcome: a dry, warmer build-up with a 1-year workmanship warranty and a happy tenant.",
  },
  {
    name: "Natalia Chronowiat",
    source: "MyBuilder",
    location: "Letchworth Garden City",
    job: "Multi-trade renovation in Letchworth (occupied)",
    rating: 5,
    date: "24 Jan 2026",
    quote:
      "Very professional, communicated clearly throughout the project, high attention to detail. We were moving in in the middle of the works, while also working from home and George made sure to always work around us, prioritise things that will help us and keep things very tidy inside the house. Very knowledgeable.",
    summary:
      "Multi-trade renovation in an occupied home (clients moving in mid-works and working from home). Outcome: clear daily communication, prioritisation of works that protected the family's routine, and a consistently tidy site.",
  },
  {
    name: "Francis",
    source: "MyBuilder",
    location: "Luton",
    job: "Loft & Kitchen Insulation / Draught-Proofing — diagnostic survey",
    rating: 5,
    date: "15 Dec 2025",
    quote:
      "George carried out a blower door test, smoke testing and thermal imaging survey at our property, and we were extremely impressed with both his professionalism and his depth of knowledge. He took the time to explain everything clearly, using the thermal images and smoke tests to show exactly where air leakage and heat loss were occurring. The final report was very detailed, well-structured and easy to understand, with clear explanations of the causes, risks and recommended remedial options. What we particularly appreciated was that George didn't just point out problems, he explained the building physics behind them and was honest and practical about priorities, costs and what would (and wouldn't) make a real difference. He was punctual, tidy, respectful of our home and very easy to communicate with throughout the process. We would have no hesitation in recommending him to anyone looking for a proper diagnostic assessment rather than a superficial inspection. Thank you again, George",
    summary:
      "Full Home Health Diagnostic Survey: blower door test + smoke tracing + thermal imaging across the loft and kitchen. Outcome: precise mapping of air-leakage and heat-loss paths, a detailed prioritised remedial plan, and an honest scope of what would (and would not) deliver real performance gains.",
  },
];

// Real thermal imaging photos from completed diagnostic surveys.
// Each image is from a real on-site FLIR survey carried out by G.A.S.
export const HEAT_LOSS_GALLERY = [
  {
    src: "https://customer-assets.emergentagent.com/job_dccf8f76-be29-4f8b-b4a5-a675ec5f8aff/artifacts/2eyiqaj8_FLIR0832.jpg",
    caption:
      "Thermal imaging of the window head and lintel junction showing reduced surface temperatures compared with the surrounding wall. The colder band indicates increased heat transfer through the window opening and highlights an area requiring further investigation.",
    range: "17.0–21.1 °C",
    tag: "Window head / lintel",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_dccf8f76-be29-4f8b-b4a5-a675ec5f8aff/artifacts/mjk8xibw_FLIR0834.jpg",
    caption:
      "Whole-window thermal survey identifying lower surface temperatures around the frame and reveals compared with adjacent wall surfaces. The thermal pattern is consistent with elevated heat loss around the window opening.",
    range: "15.7–19.7 °C",
    tag: "Window reveals",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_dccf8f76-be29-4f8b-b4a5-a675ec5f8aff/artifacts/kfda4k3h_FLIR0828.jpg",
    caption:
      "Thermal imaging of the window jamb and sill junction showing a localised cold zone at the corner detail. The reduced surface temperature indicates concentrated heat loss at the window perimeter and highlights a potential thermal weakness within the building envelope.",
    range: "15.6–19.8 °C",
    tag: "Window corner junction",
  },
];

export const THERMAL_BRIDGING_GALLERY = [
  {
    src: "https://customer-assets.emergentagent.com/job_dccf8f76-be29-4f8b-b4a5-a675ec5f8aff/artifacts/uyrva0sw_20260612_124827.jpg",
    caption:
      "Thermal-bridge modelling of the ceiling-to-wall junction with insulation continued approximately 400 mm down the wall. The extended insulation improves internal surface temperatures and reduces the concentration of heat loss at the corner.",
    tag: "Improved junction design",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_dccf8f76-be29-4f8b-b4a5-a675ec5f8aff/artifacts/rzf5faxv_20260612_125231.jpg",
    caption:
      "Thermal model illustrating the improved temperature distribution achieved by extending the insulation below ceiling level. The revised detail lowers the risk of surface condensation and mould growth at the junction.",
    tag: "Heat-flow optimisation",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_dccf8f76-be29-4f8b-b4a5-a675ec5f8aff/artifacts/8y1nnhc0_image-1781260812199.jpg",
    caption:
      "Reference simulation showing insulation installed at ceiling level only. The model highlights a stronger thermal bridge at the ceiling-to-wall junction, resulting in lower internal surface temperatures and a higher condensation risk compared with the improved design.",
    tag: "Ceiling insulation only (reference case)",
  },
];

export const THERMAL_GALLERY = [
  // Property Assessment & Costed Improvement Report project (IG8 flat) — full diagnostic survey across the flat.
  {
    src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/tjqi0d3u_FLIR1046.jpg",
    caption: "Wall / ceiling corner with vertical service pipe — concentrated cold spot at the corner junction indicating a localised thermal bridge plus a cooler signal along the pipe run",
    range: "19.1 °C → 21.5 °C",
    tag: "Thermal Bridge",
    project: "Property Assessment",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/cbmygmc9_FLIR1040.jpg",
    caption: "Internal wall surface — uniformly cool reading (18.6 °C) recorded as part of the room-by-room baseline used to compare with warmer reference surfaces in the report",
    range: "18.6 °C → 20.6 °C",
    tag: "Surface Baseline",
    project: "Property Assessment",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/8c8cng3n_FLIR1028.jpg",
    caption: "Wall / ceiling corner showing the characteristic geometric cold spot — a classic thermal bridge picked up in the assessment and flagged in the costed improvement schedule",
    range: "19.9 °C → 22.8 °C",
    tag: "Cold Spot",
    project: "Property Assessment",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/3vyf463l_FLIR1002.jpg",
    caption: "Wall / floor junction — cooler band where the slab meets the wall, evidence of perimeter heat-loss path that the report recommends targeting with perimeter insulation",
    range: "20.3 °C → 26.7 °C",
    tag: "Floor Junction",
    project: "Property Assessment",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/b2nlithx_FLIR1000.jpg",
    caption: "Internal door head and reveal next to a framed picture on a warmer external wall — pronounced cold band along the lintel, prioritised for remedial detailing in the costed report",
    range: "16.8 °C → 29.4 °C",
    tag: "Lintel Cold Band",
    project: "Property Assessment",
  },
  // Giuliano project — interior thermal survey identifying cold reveals, thermal bridges and cold spots.
  {
    src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/7xiychmp_FLIR0414.jpg",
    caption: "Living room internal wall — cold spot behind framed picture indicating localised cold-bridging where heat is conducted through the structure",
    range: "21.5 °C → 25.5 °C",
    tag: "Cold Spot",
    project: "Giuliano",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/cu9spvuc_FLIR0586.jpg",
    caption: "Wall / ceiling junction with door head — significant cold band along the lintel and reveal, classic thermal-bridge pattern combined with air leakage",
    range: "17.7 °C → 23.8 °C",
    tag: "Thermal Bridge",
    project: "Giuliano",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/s0q98upa_FLIR0606.jpg",
    caption: "Bathroom external wall — surface clearly below the rest of the room (cold reveal + cold corner) creating an elevated condensation risk on the painted finish",
    range: "15.7 °C → 22.2 °C",
    tag: "Cold Wall",
    project: "Giuliano",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/itsrbw8g_FLIR0610.jpg",
    caption: "Bathroom — door and reveal showing pronounced cold path with surface temperatures down to 14.5 °C, below internal dewpoint risk on cold days",
    range: "15.7 °C → 22.0 °C",
    tag: "Air Leakage",
    project: "Giuliano",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/k5l7buzp_FLIR0800.jpg",
    caption: "Cold wall surface vs warm ceiling — significant temperature differential",
    range: "16.8 °C → 29.5 °C",
    tag: "Heat Loss",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/k8qsjypm_FLIR0798.jpg",
    caption: "Internal wall / ceiling junction — cold-bridging at the perimeter",
    range: "17.2 °C → 22.3 °C",
    tag: "Thermal Bridge",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/g4a7cmoq_FLIR0794.jpg",
    caption: "Wall / ceiling corner showing cold spot at the junction line",
    range: "14.6 °C → 40.4 °C",
    tag: "Cold Bridge",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/gr91rx4a_FLIR0778.jpg",
    caption: "Loft / eaves zone — missing or compressed insulation visible as cold area",
    range: "17.9 °C → 24.7 °C",
    tag: "Missing Insulation",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/cp903naa_FLIR0774.jpg",
    caption: "Cold wall mapped against warm radiator and surrounding fabric",
    range: "16.8 °C → 24.2 °C",
    tag: "Cold Wall",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/lfnobzyd_FLIR0655.jpg",
    caption: "Wall / ceiling corner — concentrated cold spot indicating a localised thermal bridge",
    range: "17.5 °C → 22.6 °C",
    tag: "Thermal Bridge",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/n8otovhk_FLIR0653.jpg",
    caption: "Ceiling / external-wall junction — repeated cold spots along the perimeter line",
    range: "18.9 °C → 23.1 °C",
    tag: "Air Leakage",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/jjq31z7v_FLIR0598.jpg",
    caption: "Top of door frame — pronounced cold band consistent with air leakage around the lintel",
    range: "18.1 °C → 24.2 °C",
    tag: "Air Leakage",
  },
];

// Giuliano thermal-imaging project — four real FLIR images from his diagnostic survey.
export const GIULIANO_GALLERY = THERMAL_GALLERY.filter((g) => g.project === "Giuliano");

// Property Assessment & Costed Improvement Report project (IG8) — five FLIR images from the whole-flat survey.
export const PROPERTY_ASSESSMENT_GALLERY = THERMAL_GALLERY.filter((g) => g.project === "Property Assessment");

// Real blower door testing photos.
export const BLOWER_DOOR_GALLERY = [
  {
    src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/fkx24bvp_FLIR0582.jpg",
    caption: "Calibrated blower door fan installed in the front doorway, sealed with the red airtight panel — ready to depressurise the property for airtightness testing",
    tag: "Blower Door Installed",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/rrihkddc_FLIR0584.jpg",
    caption: "Test property — semi-detached brick home undergoing a whole-house airtightness test",
    tag: "Test Property",
  },
];

// Real smoke tracing photos — Francis (Luton) project, 12 Dec 2025.
export const SMOKE_TRACING_GALLERY = [
  {
    src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/opgn0tlk_20251212_155711.jpg",
    caption: "Smoke tracing during a blower door test — smoke escaping at the eaves of the roof, visualising the air-leakage path between the roof structure and external wall",
    tag: "Air Leakage Visualised",
  },
];

// Real brick cladding system installation photos.
export const CLADDING_GALLERY = [
  { src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/4pza2f0o_9968a293-90e4-4c71-b703-b59368365b5e.jpg", caption: "Completed brick cladding system on a contemporary new-build — light cream brick-slip façade with sharp parapet detailing and metalwork", tag: "Finished Façade" },
  { src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/4gpyl0ck_41461645-922b-492c-acd9-5f37eb9b66cb.jpg", caption: "Prefabricated brick-slip cladding panels prepared on site, ready for installation on the support rail system", tag: "Prefab Panels" },
  { src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/bn7pelcj_7ede3d5d-5aa7-44d0-b85e-ef6c1b166964.jpg", caption: "Brick-slip panels being installed onto the metal rail system over the mineral wool insulation and breather membrane — ventilated façade build-up", tag: "Rail System Install" },
  { src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/5tgimoze_fdfaf393-294f-4fcc-a120-13eb77b3e86d.jpg", caption: "Finished ventilated brick cladding elevation with drainage pebble strip at the base — protects the façade and lets the cavity drain", tag: "Finished Elevation" },
  { src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/m3eza743_7047d425-ec90-4791-8655-c8171e38d5aa.jpg", caption: "Brick-slip cladding installed onto the panel system with fresh mortar pointing — corner detail showing crisp alignment", tag: "Mortar Pointing" },
  { src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/ko76zkzc_02ae7682-b778-4557-97ba-d42dfed62f6e.jpg", caption: "Close-up of the brick-slip cladding system — brick slips bonded onto insulation panel substrate with mortar joints raked back ready for finishing", tag: "Brick Slip Detail" },
];

// Real soundproofing build-up photos — Luke Large project, March 2026.
// Acoustic floor upgrade with rockwool insulation, resilient bars and RW5 acoustic panels (new floor structure).
export const SOUNDPROOFING_GALLERY = [
  // ---- N17 project (multi-room acoustic upgrade — Checkatrade 10/10 review)
  {
    src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/k3o8zwgd_20260518_175008.jpg",
    caption: "N17 project · pink bedroom — existing floor finishes removed, joists exposed, RW5 acoustic mineral wool installed between joists with resilient bars laid across",
    tag: "RW5 Acoustic Mineral Wool",
    project: "N17",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/gbauvage_20260521_153301.jpg",
    caption: "N17 project · blue bedroom — same four-stage build-up: floor stripped, mineral wool between joists, resilient acoustic bars decoupling the new deck from the structure",
    tag: "Resilient Decoupling",
    project: "N17",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/p2za66u3_20260521_183150.jpg",
    caption: "N17 project · acoustic floor build-up mid-installation — Rockwool packs ready to be cut to joist centres, resilient bars and new boards being fixed",
    tag: "Acoustic Build-Up",
    project: "N17",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/su2kdm3s_20260526_191011.jpg",
    caption: "N17 project · finished room — acoustic underlay laid over the new floorboards, new carpet fitted, room redecorated and ready for use",
    tag: "Finished Room",
    project: "N17",
  },
  // ---- Luke Large project (E17)
  {
    src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/zwd18zzo_20260323_123026.jpg",
    caption: "Stage 1 — Victorian bedroom floor lifted, exposing the existing joists and the void below ready for the soundproofing build-up",
    tag: "Floor Lifted",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/jys8jfo3_20260323_140635.jpg",
    caption: "Stage 2 — mineral wool acoustic insulation being placed between the joists to absorb airborne noise",
    tag: "Acoustic Insulation",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/9e66o2jw_20260324_093148.jpg",
    caption: "Stage 3 — rockwool fully installed between joists with resilient acoustic bars fixed across the joists to decouple the new floor structure",
    tag: "Resilient Bars",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/ijo9urou_20260327_130343.jpg",
    caption: "Stage 4 — finished decoupled floor build-up with moisture-resistant acoustic boards (RW5 system) ready for floor finish — significant airborne and impact noise reduction achieved",
    tag: "Finished Floor",
  },
];

export const ISSUE_TYPES = [
  "Heat Loss / High Energy Bills",
  "Damp / Condensation / Mould",
  "Thermal Imaging Survey",
  "Blower Door Testing",
  "Soundproofing / Acoustics",
  "Insulation & Retrofit",
  "External Wall Insulation / Cladding",
  "MVHR & Ventilation",
  "Home Health Diagnostic Survey",
  "Other / Not Sure",
];

// N17 multi-room soundproofing project gallery (derived from SOUNDPROOFING_GALLERY).
export const N17_SOUNDPROOFING_GALLERY = SOUNDPROOFING_GALLERY.filter((g) => g.project === "N17");

// Per-service "typical findings" + "real project links" — rendered on every ServicePage.
// Educational, data-driven block. Adds depth + internal links for SEO and trust.
export const SERVICE_EXTRAS = {
  "heat-loss-investigation": {
    typicalFindings: [
      "Large continuous cold wall areas losing heat faster than the heating can replace it",
      "Poorly insulated external doors and single-skin glazing acting as major heat-loss paths",
      "Cold external-wall sections behind furniture where airflow is restricted",
      "Heat loss concentrated at wall-to-ceiling junctions and structural edges",
      "Bay windows and projecting structures losing disproportionate amounts of heat",
      "Uninsulated or under-insulated lofts, eaves and dwarf walls",
    ],
    realProjects: [
      { slug: "giuliano-internal-wall-insulation-iwi-celotex", label: "Giuliano · Shepherds Bush — Heat loss mapped with FLIR, fixed with IWI" },
      { slug: "property-assessment-costed-improvement-report", label: "IG8 · Whole-flat heat-loss survey + costed improvement report" },
    ],
  },
  "thermal-bridging-survey": {
    typicalFindings: [
      "Cold bands tracking wall-to-ceiling junctions and room corners",
      "Thermal bridges across window heads, lintels and reveals",
      "Dot-and-dab plasterboard creating insulation discontinuity and cold spots",
      "Reduced insulation continuity at the wall perimeter and structural edges",
      "Junctions running close to dewpoint — primary condensation and mould locations",
      "Repeating bridges through balconies, beams and party-wall junctions",
    ],
    realProjects: [
      { slug: "giuliano-internal-wall-insulation-iwi-celotex", label: "Giuliano · Shepherds Bush — Cold junction & IWI thermal-bridge remediation" },
      { slug: "property-assessment-costed-improvement-report", label: "IG8 · Whole-flat thermal-bridge & condensation-risk survey" },
    ],
  },
  "home-health-diagnostics": {
    typicalFindings: [
      "Cold reveals around windows and external door heads",
      "Air leakage through unsealed loft hatches and service penetrations",
      "Localised thermal bridges at wall/ceiling and wall/floor junctions",
      "Hidden moisture trapped within wall or floor build-ups",
      "Inadequate extract ventilation in bathrooms and kitchens",
      "Indoor humidity above 65% RH driving condensation on cold surfaces",
      "Previous retrofit insulation installed with workmanship gaps",
    ],
    realProjects: [
      { slug: "francis-luton-blower-door-thermal-imaging", label: "Francis · Luton — Blower Door + Smoke Tracing + Thermal Imaging" },
      { slug: "property-assessment-costed-improvement-report", label: "IG8 · Whole-flat Property Assessment & Costed Improvement Report" },
    ],
  },
  "thermal-imaging": {
    typicalFindings: [
      "Cold spots behind picture frames, wardrobes and sofas — early condensation risk",
      "Thermal bridges along door-head lintels and wall/ceiling reveals",
      "Missing or compressed insulation in lofts, eaves and dormers",
      "Air leakage paths visible as cold streams from skirtings and service penetrations",
      "Cold corners in bathrooms and external-wall reveals (condensation hotspots)",
      "Evaporative cooling signatures indicating hidden moisture in wall fabric",
    ],
    realProjects: [
      { slug: "giuliano-internal-wall-insulation-iwi-celotex", label: "Giuliano · Shepherds Bush — IWI specified using FLIR diagnostic data" },
      { slug: "property-assessment-costed-improvement-report", label: "IG8 · Whole-flat thermal imaging survey + report" },
    ],
  },
  "blower-door-testing": {
    typicalFindings: [
      "Loft hatch and ceiling penetrations leaking at >10× allowed rate",
      "Window and door reveals leaking around poorly sealed perimeters",
      "Service penetrations (waste pipes, cables) unsealed at external walls",
      "Suspended ground-floor perimeters open to ventilated subfloor voids",
      "Chimney flues and party-wall bypasses acting as constant leakage paths",
      "Smoke tracing visualises every leak pathway — quantified at 50 Pa",
    ],
    realProjects: [
      { slug: "francis-luton-blower-door-thermal-imaging", label: "Francis · Luton — Full blower door + smoke tracing investigation" },
    ],
  },
  "damp-moisture-condensation": {
    typicalFindings: [
      "Cold internal wall surfaces below dewpoint of indoor air",
      "Condensation behind wardrobes and sofas positioned against external walls",
      "Interstitial condensation risk within retrofit insulation build-ups",
      "Moisture trapped behind impermeable finishes (gloss paint, vinyl wallpaper)",
      "Penetrating damp at failed pointing, blocked airbricks or cracked rendering",
      "High indoor humidity from indoor laundry drying or inadequate kitchen extract",
    ],
    realProjects: [
      { slug: "giuliano-internal-wall-insulation-iwi-celotex", label: "Giuliano · Cold cupboard damp & mould eliminated with IWI + thermal verification" },
    ],
  },
  "soundproofing-acoustics": {
    typicalFindings: [
      "Sound transmission through suspended timber floors via the joists (flanking)",
      "Impact noise (footsteps, dropped objects) bypassing carpet alone",
      "Airborne noise (voices, TV) through wall/floor junctions with no decoupling",
      "Existing floors with no insulation between joists and no acoustic decoupling",
      "Party-wall junctions transmitting both airborne and impact noise",
      "Inadequate construction detailing where flanking paths short-circuit the build-up",
    ],
    realProjects: [
      { slug: "n17-soundproofing-multi-room-acoustic-upgrade", label: "N17 · Multi-room acoustic floor build-up + wall upgrade (10/10 Checkatrade)" },
      { slug: "luke-large-underfloor-insulation-soundproofing", label: "Luke Large · E17 — Underfloor insulation + acoustic floor" },
    ],
  },
  "insulation-retrofit": {
    typicalFindings: [
      "Cold internal walls and external-wall corners sitting below dewpoint",
      "Cold floors caused by uninsulated suspended-timber voids",
      "Loft insulation compressed below 100 mm, displaced around eaves",
      "Thermal bridges left untreated at wall/floor and wall/ceiling junctions",
      "Existing retrofit with workmanship gaps, no airtightness layer continuity",
    ],
    realProjects: [
      { slug: "giuliano-internal-wall-insulation-iwi-celotex", label: "Giuliano · IWI (Celotex/Kingspan equiv.) to cold cupboard" },
      { slug: "luke-large-underfloor-insulation-soundproofing", label: "Luke Large · Underfloor insulation between joists" },
    ],
  },
  "external-wall-insulation-cladding": {
    typicalFindings: [
      "Solid-wall homes losing 35–45% of their heat through external walls",
      "Cold external-wall surfaces creating condensation in bedrooms",
      "Existing renders cracked, allowing moisture ingress",
      "Brick-slip cladding systems with poor support-rail detailing",
      "Thermal bridges at openings (window reveals, lintels) reducing real-world performance",
    ],
    realProjects: [],
  },
  "mvhr-ventilation": {
    typicalFindings: [
      "Bathrooms with intermittent extract fans delivering <50% of required airflow",
      "Kitchens producing 2–4 kg/day of moisture with no continuous extract",
      "Trickle vents closed or blocked, no continuous fresh-air supply",
      "Airtight retrofits without balanced ventilation — CO₂ regularly >1500 ppm",
    ],
    realProjects: [],
  },
  "indoor-air-quality": {
    typicalFindings: [
      "Bedroom CO₂ levels >2000 ppm overnight (fatigue, poor sleep)",
      "Indoor humidity above 70% RH driving condensation and mould",
      "VOC and formaldehyde from new finishes elevated for weeks after works",
      "PM2.5 spikes from cooking, candles and unflued appliances",
    ],
    realProjects: [],
  },
  "heat-pump-readiness": {
    typicalFindings: [
      "Whole-house heat loss far higher than a desktop estimate assumed",
      "Existing radiators undersized for the low flow temperatures a heat pump needs",
      "Significant air leakage inflating the heat demand and the required system size",
      "Uninsulated or under-insulated walls, floors and lofts dominating the heat loss",
      "Cold bridges keeping rooms cool at low flow temperatures",
      "Microbore or restrictive pipework limiting achievable flow rates",
    ],
    realProjects: [
      { slug: "property-assessment-costed-improvement-report", label: "IG8 · Whole-flat measured heat-loss survey + costed improvement report" },
    ],
  },
};


// =============================================================================
// Extended regional coverage — beyond London into Greater London + Home Counties.
// Used in trust strips, footer copy and structured-data localAreaServed.
// =============================================================================
export const REGIONS_COVERED = [
  "London",
  "Greater London",
  "East London",
  "Essex",
  "Kent",
  "Surrey",
  "Hertfordshire",
];

// =============================================================================
// Deep educational deep-dive content per service-slug.
// Positions RetrofitIQ as a Building Performance Diagnostics company — every
// service is broken into: What it is · Problems it solves · How we investigate ·
// Equipment used · Building physics · Benefits · Process · Trust badges.
// Rendered by ServicePage.jsx after the existing FAQ / Typical Findings block.
// =============================================================================
export const EDUCATIONAL_DEEP_DIVE = {
  "heat-loss-investigation": {
    whatItIs:
      "A heat loss investigation uses calibrated FLIR thermal imaging to measure the surface temperature of your building's fabric and reveal exactly where heat is escaping. Rather than guessing from your energy bills, we map the cold surfaces, doors, glazing and junctions that are draining heat — and quantify which ones matter most.",
    problems: [
      "Rooms that never feel warm no matter how high the heating is set",
      "Heating bills far higher than comparable homes",
      "Cold walls, draughts and uncomfortable spots near windows and doors",
      "A new boiler or more radiators that didn't fix the cold",
      "Suspected missing or under-performing insulation",
      "Cold, hard-to-heat extensions, bays and loft rooms",
    ],
    investigation: [
      "Walkthrough to record where the home feels coldest and why",
      "Internal FLIR thermal imaging under a stabilised indoor/outdoor temperature differential (≥10 °C)",
      "External thermal scans (where access allows) to confirm fabric heat loss",
      "Surface-temperature comparison between affected areas and sound reference areas",
      "Findings interpreted against construction type, age and any previous works",
    ],
    equipment: [
      "FLIR thermal imaging camera (calibrated, radiometric)",
      "Surface-temperature probe and infrared spot thermometer",
      "Humidity / dewpoint logger to relate heat loss to condensation risk",
      "Blower door fan (depressurisation reveals draught-driven heat loss)",
    ],
    buildingPhysics:
      "Heat always flows from warm to cold, through conduction, convection and radiation. The greater the temperature difference and the poorer the insulation, the faster heat escapes. A thermal camera reads the radiation from each surface — so insulation gaps, cold bridges and leaky openings appear as cold patches, showing precisely where, and how fast, your home is losing heat.",
    benefits: [
      "Know exactly where heat is being lost — before spending on remediation",
      "Target the fabric improvements that deliver the biggest comfort and bill savings",
      "Avoid wasted spend on the wrong upgrades",
      "Measured evidence for retrofit planning, grants or disputes",
      "A clear fabric-first plan from a Certified Passive House Designer",
    ],
  },
  "thermal-bridging-survey": {
    whatItIs:
      "A thermal bridging survey uses calibrated FLIR thermal imaging to find the localised cold paths — thermal bridges — where heat shortcuts through the structure, bypassing the insulation. These junctions, lintels, reveals and corners lose heat and, crucially, are the coldest surfaces in the room, making them the first place condensation and mould appear.",
    problems: [
      "Black mould or condensation that keeps returning in the same corner",
      "Cold patches at ceilings, corners and around windows",
      "Damp reveals and window surrounds despite a dry room",
      "Mould reappearing after insulation works elsewhere",
      "Cold junctions in extensions, dormers and balconies",
      "Uncertainty over whether a cold spot is a bridge or missing insulation",
    ],
    investigation: [
      "Walkthrough to record where condensation, mould or cold corners occur",
      "Internal FLIR thermal imaging under a stabilised temperature differential",
      "Surface-temperature readings at each junction compared against measured dewpoint",
      "Assessment of whether each bridge is geometric, repeating or workmanship-related",
      "Findings related to construction type, junction detail and any previous works",
    ],
    equipment: [
      "FLIR thermal imaging camera (calibrated, radiometric, with emissivity correction)",
      "Surface-temperature probe and infrared spot thermometer",
      "Humidity / dewpoint logger to quantify condensation risk at each junction",
      "High-resolution visual camera for paired thermal + visual reporting",
    ],
    buildingPhysics:
      "A thermal bridge is a path of lower thermal resistance through the building envelope — a corner, lintel or structural element where heat flows faster than through the surrounding insulated fabric. The result is a localised cold surface. When that surface drops below the dewpoint of the indoor air, moisture condenses there first — which is why thermal bridges and mould go hand in hand.",
    benefits: [
      "Pinpoint the exact junctions driving recurring condensation and mould",
      "Understand whether the cause is geometry, structure or workmanship",
      "Target remediation to restore insulation continuity where it matters",
      "Reduce heat loss and eliminate the coldest surfaces in the room",
      "Measured evidence for retrofit design, disputes or new-build defects",
    ],
  },
  "thermal-imaging": {
    whatItIs:
      "A thermal imaging survey uses a calibrated FLIR infrared camera to measure the surface temperature of every wall, ceiling, floor, window and reveal in your home. Cold patches, missing insulation, thermal bridges and hidden air leakage become visible — exactly where they are, at the temperature they are.",
    problems: [
      "Cold rooms and uneven heating performance",
      "Persistent damp patches and mould in cold corners",
      "Drafts, cold reveals and uncomfortable rooms in winter",
      "Sky-high heating bills with no obvious cause",
      "Suspected missing or compressed insulation in walls, lofts or floors",
      "Hidden thermal bridges driving condensation risk",
    ],
    investigation: [
      "On-site walkthrough with the homeowner to map symptoms room by room",
      "Internal FLIR thermal imaging under a stabilised indoor/outdoor temperature differential (≥10 °C)",
      "External thermal scans (where line-of-sight allows) to corroborate insulation gaps",
      "Surface temperature probe checks against measured dewpoint",
      "All findings cross-referenced with construction type, age and previous works",
    ],
    equipment: [
      "FLIR thermal imaging camera (calibrated, radiometric, with emissivity correction)",
      "Surface temperature probe & infrared spot thermometer",
      "Humidity / dewpoint logger for condensation-risk assessment",
      "High-resolution visual camera for paired thermal + visual reporting",
      "Blower door fan (depressurisation enhances air-leakage visibility on the thermal image)",
    ],
    buildingPhysics:
      "Heat moves from warm to cold by conduction, convection and radiation. A thermal camera measures the radiation emitted by every surface and converts it to a temperature reading. Insulation gaps, wet plaster, air leakage and thermal bridges all show up as colder (or warmer) patches than the surrounding surface — that's how we read the building.",
    benefits: [
      "Identify the root cause of cold spots, damp and high energy bills",
      "Pinpoint missing or compressed insulation without invasive work",
      "Quantify thermal bridge risk and condensation hotspots",
      "Provide measured evidence for insurance claims, party wall disputes or new-build defects",
      "Inform a focused, building-physics-led retrofit specification",
    ],
  },
  "blower-door-testing": {
    whatItIs:
      "A blower door test fits a calibrated fan into an external door, pressurises (or depressurises) the building, and measures how much air leaks through the envelope. The result is an air change rate at 50 Pa (ACH₅₀) and a measured airflow (m³/h) — the same metric used in Passive House and Part L compliance.",
    problems: [
      "Cold draughts you can feel even with the heating on",
      "Sounds (and smells) bleeding through between flats",
      "New-build air-tightness failing Part L",
      "Energy bills 20–40% higher than expected",
      "Uncontrolled air-leakage undermining a brand-new MVHR system",
      "Hidden bypass paths in lofts, service penetrations and intermediate floors",
    ],
    investigation: [
      "Pre-test walkthrough — seal intentional openings (vents, flues, MVHR)",
      "Pressurisation + depressurisation test (typically 25–70 Pa range, single-point and multi-point)",
      "Simultaneous smoke tracing to visualise the actual leakage paths",
      "Thermal imaging under depressurisation to confirm cold-air ingress",
      "Itemised leak inventory with photos, locations and remediation priorities",
    ],
    equipment: [
      "Minneapolis-style calibrated blower door fan + frame + shroud",
      "Manometer / pressure transducer with software logging (TECTITE / TEC ducted air-flow analyser equivalent)",
      "Smoke tracer pen / theatrical fog machine for leak visualisation",
      "FLIR thermal camera for paired thermal-under-pressure imaging",
      "Anemometer for spot velocity readings at suspect leakage points",
    ],
    buildingPhysics:
      "Air movement through the envelope carries heat (convective loss), moisture (vapour) and noise. Pressurising the building amplifies pressure differences across every crack, gap and penetration, so even a 1 mm gap that's invisible under normal conditions becomes a measurable, locatable air-leakage path. That's how you separate symptom from cause.",
    benefits: [
      "Measured airtightness in ACH₅₀ / m³/h — not assumed",
      "Locate every meaningful air-leakage path with smoke + thermal evidence",
      "Quantify the heating-energy penalty of uncontrolled infiltration",
      "Verify that MVHR will actually deliver controlled, balanced ventilation",
      "Provide compliance evidence for Part L, Passive House, AECB and Enerphit",
    ],
  },
  "soundproofing-acoustics": {
    whatItIs:
      "Acoustic diagnostics measure how sound actually moves through your floors, walls and ceilings — airborne (voices, TV, traffic) and impact (footfall, doors, drops). A measured assessment tells you which build-up will actually solve the problem, instead of guessing with mass-loaded vinyl or off-the-shelf foam.",
    problems: [
      "Footfall noise from upstairs neighbours through timber floors",
      "TV / music bleed-through between flats or party walls",
      "Conversations clearly audible through stud walls",
      "Reverberant, echoey rooms in hard-finished interiors",
      "Building Regs Approved Document E failures on flat conversions",
      "Home cinemas, music rooms and offices needing measurable isolation",
    ],
    investigation: [
      "Listen-walk with the occupant to identify dominant noise sources and paths",
      "Sound pressure level (dBA) measurement of the offending noise at the receiving location",
      "Identify flanking paths — joist ends, party walls, service voids, partition penetrations",
      "Define target Rw / DnT,w / L'nT,w improvement (in dB) based on the use case",
      "Specify a decoupled mass-spring-mass build-up matched to the construction",
    ],
    equipment: [
      "Class 1 sound level meter with octave-band analysis",
      "Impact hammer / tapping machine for floor impact tests",
      "Speaker + signal generator for partition airborne tests",
      "Reverberation time logger for room-acoustic assessments",
      "Borescope for confirming wall / floor / ceiling build-ups before specifying",
    ],
    buildingPhysics:
      "Sound is transmitted by vibration. To reduce it you need three things working together: mass (heavy layers that resist movement), decoupling (resilient bars or isolation hangers that break the rigid path) and absorption (mineral wool inside the cavity to dissipate trapped sound energy). Get one of the three wrong and the result barely changes — that's why so many DIY soundproofing jobs fail.",
    benefits: [
      "Measured noise reduction (in dB) — not subjective improvement",
      "Mass-spring-mass build-ups designed for your specific construction",
      "Flanking paths identified and sealed — the single biggest failure point",
      "Compliance with Approved Document E (or better) for flat conversions",
      "A documented before/after sound test as proof",
    ],
  },
  "insulation-retrofit": {
    whatItIs:
      "Internal Wall Insulation (IWI) bonds a high-performance insulation layer to the inside of an existing solid wall, dramatically improving its U-value while keeping the external appearance untouched. Specified incorrectly, IWI causes interstitial condensation and hidden mould. Specified properly — with measured data — it transforms comfort and energy use.",
    problems: [
      "Cold, solid-wall Victorian and Edwardian homes losing heat through every external wall",
      "Condensation forming on internal wall surfaces in winter",
      "Conservation areas where External Wall Insulation isn't permitted",
      "Cold reveals and lintels driving mould around windows",
      "Homes where existing IWI has failed (peeling paint, hidden damp, black mould)",
      "Solid-wall flats where only the occupied unit is being upgraded",
    ],
    investigation: [
      "Full thermal-imaging survey to baseline wall surface temperatures",
      "Moisture-meter readings on walls, skirting, plaster and floor junctions",
      "Surface humidity + dewpoint logging in the affected rooms",
      "Construction build-up confirmed by borescope or test opening",
      "Condensation-risk modelling (WUFI / Glaser) on the proposed IWI build-up before any work",
    ],
    equipment: [
      "FLIR thermal camera — to map cold surfaces and reveal cold bridges",
      "Pin-type and pinless moisture meter (multiple substrates)",
      "Surface temperature probe + RH/dewpoint logger",
      "Borescope camera for non-invasive build-up confirmation",
      "Hygrothermal modelling software for vapour-flow simulation",
    ],
    buildingPhysics:
      "Insulating the inside of a wall makes the original masonry colder. Warm, moist internal air must NOT reach that cold masonry — otherwise vapour condenses inside the build-up. That's why a properly designed IWI system uses a vapour control layer, airtight detailing at every junction, and a hygric-safe insulation (wood fibre / PIR with VCL / aerogel) appropriate to the wall. Get the physics wrong and you build a hidden mould factory.",
    benefits: [
      "U-values typically improving from 2.0–2.2 W/m²K → 0.30–0.40 W/m²K",
      "Internal wall surface temperatures lifted by 4–8 °C in winter",
      "Condensation and mould risk on internal surfaces eliminated",
      "Heating energy use on the upgraded walls reduced by ~60–80%",
      "Compatible with conservation areas, listed buildings and flat-only retrofits",
    ],
  },
  "external-wall-insulation-cladding": {
    whatItIs:
      "External Wall Insulation (EWI) wraps the outside of the building in a continuous insulation layer, eliminating cold spots and thermal bridges in one move. Combined with a render, brick-slip or ventilated cladding finish, it's the highest-performing solid-wall retrofit option available — and the only one that fully protects the original masonry from frost and driving rain.",
    problems: [
      "Solid-wall homes with poor U-values and high energy bills",
      "Cold-bridging at lintels, jambs, eaves and intermediate floors",
      "Tired or damp masonry needing weatherproofing as well as thermal upgrade",
      "Mid-century non-traditional construction (concrete panel, no-fines) with chronic cold-bridging",
      "Estates and HMOs needing a uniform, low-maintenance external upgrade",
      "Conservation-area-friendly brick-slip systems where rendered EWI isn't appropriate",
    ],
    investigation: [
      "External thermal scan to map heat-loss patterns wall by wall",
      "Construction survey — masonry condition, frost damage, render adhesion",
      "Wind / driving-rain exposure category check (BS 8104)",
      "Reveal, sill, eaves and roof-junction detailing planned for thermal continuity",
      "Ventilated cavity vs. closed render system selected on building-physics basis",
    ],
    equipment: [
      "FLIR thermal camera (external, paired with internal-side verification)",
      "Pull-off adhesion tester for existing render assessment",
      "Moisture meter + RH probes for masonry condition",
      "Laser distance + level for substrate setting-out",
      "Mechanical anchor pull-out tester for fixing specification",
    ],
    buildingPhysics:
      "An external insulation layer keeps the original masonry warm — well above dewpoint — so condensation forms in the (vapour-open) outer finish instead of inside the wall. Continuous insulation also bridges every previous cold-bridge: lintels, sills, intermediate floors and party-wall junctions are wrapped in one move. Done properly, EWI is the most thermally-efficient solid-wall retrofit by a wide margin.",
    benefits: [
      "U-values typically improving from 2.0–2.2 W/m²K → 0.20–0.28 W/m²K",
      "Continuous insulation — thermal bridges essentially eliminated",
      "Original masonry kept warm and dry, extending its life",
      "Façade refresh: render, brick slip or ventilated cladding — your choice",
      "No loss of internal floor area (a major IWI trade-off)",
    ],
  },
  "mvhr-ventilation": {
    whatItIs:
      "Mechanical Ventilation with Heat Recovery (MVHR) constantly extracts moist, stale air from kitchens and bathrooms and supplies filtered fresh air to bedrooms and living rooms — recovering 80–93% of the heat in the process. It's the only ventilation strategy that delivers controlled, balanced fresh air without throwing your heating out of the window.",
    problems: [
      "Airtight new builds and deep retrofits where opening windows isn't enough",
      "Bathrooms with persistent condensation and mould",
      "Bedrooms with CO₂ >1500 ppm overnight (fatigue, poor sleep)",
      "Indoor humidity above 65% RH driving dust mites and mould",
      "Hayfever / asthma sufferers needing filtered fresh air",
      "Properties next to busy roads with poor outdoor air quality",
    ],
    investigation: [
      "Whole-house airflow demand calculation per Approved Doc F and PHPP",
      "Duct route planning with thermal-bridge-free penetrations through the envelope",
      "Heat-exchanger sizing, SFP (specific fan power) and acoustic spec",
      "Filter strategy (G4 + F7 / ePM1 50%) for outdoor pollutants",
      "Commissioning and balancing plan (room-by-room flow targets)",
    ],
    equipment: [
      "Anemometer + balometer for room-by-room airflow verification",
      "CO₂ + humidity logger to verify ventilation performance over time",
      "Acoustic measurement at supply / extract terminals (Class 1 SLM)",
      "Smoke pen for air-pattern visualisation",
      "Manometer for filter pressure drop monitoring",
    ],
    buildingPhysics:
      "A heat-exchanger places incoming cold fresh air on one side of a thin membrane, and outgoing warm stale air on the other. They never mix — but heat transfers across the membrane, so the fresh air entering your bedrooms is already pre-warmed to within a few degrees of room temperature. The result: continuous fresh air with almost no heating penalty.",
    benefits: [
      "Continuous controlled fresh air (every room, every hour)",
      "80–93% heat recovery — no heating energy thrown away",
      "Filtered intake (F7 / ePM1 50%) — pollen, soot, PM2.5 removed",
      "Bedroom CO₂ stays below 1000 ppm — measurably better sleep",
      "Indoor humidity controlled to 40–60% RH — condensation eliminated",
    ],
  },
  "indoor-air-quality": {
    whatItIs:
      "An Indoor Air Quality (IAQ) assessment measures the air you actually breathe — CO₂, humidity, VOCs, formaldehyde, PM2.5, radon and temperature — over a representative period. Most homes have never been measured. Once you see the data, the right ventilation, filtration and source-control strategy becomes obvious.",
    problems: [
      "Headaches, fatigue, brain fog or poor sleep with no obvious cause",
      "Asthma, hayfever or eczema symptoms worse indoors than outdoors",
      "Condensation, mould and high humidity rooms",
      "Newly-renovated homes with strong off-gassing smells (paint, MDF, glues)",
      "Properties near major roads, railways or industrial sources",
      "Schools, offices and HMOs needing IAQ evidence for occupant welfare",
    ],
    investigation: [
      "Continuous IAQ logging (CO₂, RH, T, PM2.5, VOCs) for 7–14 days",
      "Spot measurements of formaldehyde, NO₂ and radon where indicated",
      "Source-by-source pollutant inventory (cooking, candles, MDF, cleaners, biomass)",
      "Ventilation rate verification (anemometer / blower door + tracer-gas)",
      "Prioritised IAQ improvement plan: source control → ventilation → filtration",
    ],
    equipment: [
      "Multi-parameter IAQ logger (CO₂ NDIR, RH, T, PM2.5 laser, VOC PID)",
      "Formaldehyde / NO₂ passive samplers (where indicated)",
      "Radon detector (long-duration where geology requires)",
      "Anemometer + balometer for ventilation verification",
      "Particle counter for filtration-strategy testing",
    ],
    buildingPhysics:
      "Indoor air is typically 2–5× more polluted than outdoor air. Pollutants enter from outside (PM2.5, NO₂, pollen), are generated inside (cooking, cleaning, off-gassing, breathing) and are diluted (or trapped) by ventilation. Without measurement you're guessing. With measurement you know exactly which lever — source control, ventilation or filtration — to pull.",
    benefits: [
      "Measured CO₂, humidity, PM2.5, VOC and formaldehyde data — not assumptions",
      "Prioritised, evidence-based IAQ improvement plan",
      "Better sleep, fewer headaches, clearer thinking — documented",
      "Asthma and allergy triggers identified and removed",
      "A re-measured IAQ report after improvements as verification",
    ],
  },
  "home-health-diagnostics": {
    whatItIs:
      "A Home Health Diagnostic Survey is our complete, whole-building investigation. Rather than chasing one symptom, we combine calibrated FLIR thermal imaging, blower-door airtightness testing, moisture and humidity logging, ventilation checks and a room-by-room inspection to build a measured picture of how the entire home performs — and to pinpoint the root causes of cold, damp, condensation, draughts and poor air quality.",
    problems: [
      "Several symptoms at once — cold rooms, damp, draughts and high bills — with no single cause",
      "Mould or condensation appearing in more than one room",
      "Previous works (windows, insulation, a new boiler) that did not deliver",
      "A property being bought, let or retrofitted where real performance is unknown",
      "Tenant complaints needing independent, evidence-based investigation",
      "A home that simply never feels comfortable however it is heated",
    ],
    investigation: [
      "Room-by-room walkthrough and occupant interview to map every reported symptom",
      "FLIR thermal imaging across walls, ceilings, floors, reveals and junctions",
      "Blower-door airtightness test with smoke tracing of leakage paths (where applicable)",
      "Moisture-meter readings and surface-temperature checks against measured dew point",
      "Indoor humidity, temperature and (where relevant) CO₂ logging",
      "Findings interpreted against construction type, age and previous works",
    ],
    equipment: [
      "Calibrated FLIR thermal imaging camera (radiometric)",
      "Minneapolis-style calibrated blower door fan and manometer",
      "Pin and pinless moisture meters for multiple substrates",
      "Temperature, humidity and dew-point data loggers",
      "Smoke pen / fogger for air-leakage visualisation",
      "IAQ logger (CO₂, PM2.5, VOC) where air quality is a concern",
    ],
    buildingPhysics:
      "Heat loss, air leakage, moisture and ventilation are not separate problems — they are one interacting system. A cold surface, high indoor humidity and poor ventilation together produce condensation and mould; air leakage carries both heat and moisture into the fabric. Measuring all four at once is the only way to see how they combine in your specific building, which is why a whole-home diagnostic finds causes that single-issue surveys miss.",
    benefits: [
      "One measured picture of how the whole home performs",
      "Root causes identified, not just the visible symptoms",
      "A single prioritised, costed schedule of improvements",
      "A fabric-first roadmap that can be staged across budgets",
      "An independent report for architects, installers, surveyors or landlords",
      "Optional verification after works to prove the improvement",
    ],
  },
  "damp-moisture-condensation": {
    whatItIs:
      "A damp, moisture and condensation investigation is a forensic, measurement-led diagnosis of why a wall, ceiling or room is wet or growing mould. Because surface condensation, penetrating damp, interstitial condensation and rising damp look identical but need completely different remedies, we measure before we recommend — combining thermal imaging, moisture readings, humidity and dew-point analysis and an external envelope inspection to identify the true mechanism.",
    problems: [
      "Black mould that returns after every clean or repaint",
      "Streaming windows and wet, cold wall surfaces in winter",
      "Damp patches behind furniture against external walls",
      "A musty smell with no visible source",
      "Peeling paint, blown plaster or low-level tide marks",
      "A previous 'damp-proofing' treatment that did not work",
    ],
    investigation: [
      "Visual inspection inside and out, plus occupant interview",
      "FLIR thermal imaging of every affected room to map cold surfaces",
      "Surface-temperature readings compared against measured indoor dew point",
      "Moisture-meter readings on plaster, masonry, skirting and floor junctions",
      "Indoor humidity and temperature logging (often over 7–14 days for chronic cases)",
      "External-envelope inspection for water-entry paths (pointing, render, gutters, airbricks)",
    ],
    equipment: [
      "Calibrated FLIR thermal imaging camera",
      "Pin-type and pinless / capacitance moisture meters",
      "Surface-temperature probe and infrared spot thermometer",
      "Humidity / dew-point data loggers",
      "Borescope for non-invasive build-up inspection",
      "Salts test kit to identify hygroscopic false readings",
    ],
    buildingPhysics:
      "Condensation forms when a surface falls below the dew point of the indoor air — air at 20 °C and 60% RH condenses on any surface below about 12 °C. Cold walls, single glazing, lintels and thermal bridges routinely fall below dew point in winter, especially when occupant moisture loads are high and ventilation is poor. Penetrating and interstitial damp are different mechanisms entirely, driven by water entry and vapour movement through the build-up. Separating them requires measuring surface temperature, moisture and dew point together — no single reading is diagnostic on its own.",
    benefits: [
      "A clear diagnosis of which damp mechanism is responsible, and where",
      "Measured evidence rather than a sales-led product recommendation",
      "Independent advice — we do not sell injected damp-proof courses",
      "A prioritised, costed remediation plan that fixes the cause",
      "Evidence-grade reporting for landlords, insurers and disputes",
      "Optional follow-up verification once remediation is complete",
    ],
  },
  "heat-pump-readiness": {
    whatItIs:
      "A Heat Pump Readiness Assessment establishes, before you commit, whether your home can run a heat pump efficiently. We carry out a measured room-by-room heat-loss survey, assess the fabric and airtightness, and review whether your existing emitters can deliver heat at the low flow temperatures a heat pump needs — so the system is sized correctly and runs efficiently, rather than hot, expensive and disappointing.",
    problems: [
      "Considering a heat pump but unsure the home can run one efficiently",
      "Radiators that already struggle to heat rooms on cold days",
      "Cold rooms, draughts and high bills hinting at significant heat loss",
      "An installer quote based on a desktop estimate, not a measured survey",
      "Uncertainty about which fabric upgrades are needed first",
      "A wish to avoid an oversized, noisy or costly-to-run system",
    ],
    investigation: [
      "Measured room-by-room heat-loss survey using a fabric assessment, not a desktop estimate",
      "Thermal imaging and airtightness checks to quantify real losses",
      "Review of wall, roof and floor insulation and the realistic upgrades available",
      "Emitter survey — whether existing radiators / underfloor can run at low flow temperatures",
      "Assessment of ventilation and moisture behaviour as the fabric is tightened",
      "Modelling of the heat demand a low-temperature system would need to meet",
    ],
    equipment: [
      "Calibrated FLIR thermal imaging camera",
      "Blower door fan for airtightness assessment",
      "Moisture meters and surface-temperature probes",
      "Temperature and humidity data loggers",
      "Fabric U-value and heat-loss modelling tools",
    ],
    buildingPhysics:
      "Heat pumps achieve their high efficiency (a COP of 3–4) by delivering heat at low flow temperatures — typically 35–45 °C rather than the 60–70 °C of a gas boiler. That only works if the building's heat loss is low enough for large, low-temperature emitters to keep rooms warm. Reduce heat loss through fabric and airtightness first and you can size a smaller, cheaper, quieter heat pump that runs efficiently; skip that step and the system runs hard, costs more and underperforms. Fabric first, heat pump second is building physics, not preference.",
    benefits: [
      "A measured whole-house heat-loss figure and an honest readiness verdict",
      "A prioritised, fabric-first plan to reach the required heat demand",
      "Guidance on the realistic flow temperature your home could run at",
      "Emitter and distribution recommendations for low-temperature heating",
      "A smaller, cheaper, quieter and more efficient system once ready",
      "An independent report to hand to your installer or MCS designer",
    ],
  },
};

// =============================================================================
// Knowledge Centre — building-science articles for organic SEO + authority.
// Each article has slug, category, title, lead, sections, related services
// and a publish date. New articles drop into this list and the index updates.
// =============================================================================
export const KNOWLEDGE_ARTICLES = [
  {
    slug: "what-is-passive-house",
    category: "Passive House",
    cluster: "passive-house",
    title: "What is Passive House? The complete UK guide.",
    lead: "Not a brand or a product — a set of physics-based, measured performance targets. The standard, the building science and why it underpins every retrofit we design.",
    publishedISO: "2026-06-02",
    readingMinutes: 12,
    related: ["home-health-diagnostics", "blower-door-testing", "insulation-retrofit"],
    external: false,
  },
  {
    slug: "five-passive-house-principles",
    category: "Passive House",
    cluster: "passive-house",
    title: "The five Passive House principles, explained.",
    lead: "Continuous insulation, thermal-bridge-free detailing, airtightness, high-performance glazing and MVHR — one interdependent system, not a shopping list.",
    publishedISO: "2026-06-03",
    readingMinutes: 13,
    related: ["insulation-retrofit", "blower-door-testing", "mvhr-ventilation", "thermal-imaging"],
    external: false,
  },
  {
    slug: "thermal-bridge-free-design",
    category: "Passive House",
    cluster: "passive-house",
    title: "Thermal-bridge-free design — the physics of cold spots.",
    lead: "Why insulation must be continuous, how ψ-values quantify thermal bridges, and how cold junctions cause the condensation and mould that recur in the same corners.",
    publishedISO: "2026-06-04",
    readingMinutes: 12,
    related: ["thermal-imaging", "insulation-retrofit", "damp-moisture-condensation"],
    external: false,
  },
  {
    slug: "high-performance-windows",
    category: "Passive House",
    cluster: "passive-house",
    title: "High-performance windows — U-values, glazing and comfort.",
    lead: "Ug vs Uf vs Uw, triple glazing, warm-edge spacers, g-value and solar gain, installation in the insulation plane — and the comfort criterion behind the numbers.",
    publishedISO: "2026-06-05",
    readingMinutes: 11,
    related: ["thermal-imaging", "insulation-retrofit", "home-health-diagnostics"],
    external: false,
  },
  {
    slug: "mvhr-explained",
    category: "Passive House",
    cluster: "passive-house",
    title: "MVHR explained — mechanical ventilation with heat recovery.",
    lead: "How the heat exchanger recovers 75–95% of ventilation heat, why airtightness is its prerequisite, and what separates a silent, healthy system from a noisy, ineffective one.",
    publishedISO: "2026-06-06",
    readingMinutes: 12,
    related: ["mvhr-ventilation", "indoor-air-quality", "blower-door-testing"],
    external: false,
  },
  {
    slug: "phpp-explained",
    category: "Passive House",
    cluster: "passive-house",
    title: "PHPP explained — how Passive House energy is calculated.",
    lead: "The energy-balance model that predicts heating demand, comfort and overheating before a brick is laid — and why it closes the performance gap that SAP leaves open.",
    publishedISO: "2026-06-07",
    readingMinutes: 11,
    related: ["home-health-diagnostics", "insulation-retrofit", "blower-door-testing"],
    external: false,
  },
  {
    slug: "passive-house-vs-building-regulations",
    category: "Passive House",
    cluster: "passive-house",
    title: "Passive House vs Building Regulations — the performance gap.",
    lead: "Why a Part L-compliant home is not the same as a low-energy one: airtightness limits, SAP vs PHPP, and the documented gap between predicted and actual energy use.",
    publishedISO: "2026-06-08",
    readingMinutes: 10,
    related: ["blower-door-testing", "home-health-diagnostics", "insulation-retrofit"],
    external: false,
  },
  {
    slug: "enerphit-retrofit",
    category: "Passive House",
    cluster: "passive-house",
    title: "EnerPHit — Passive House principles applied to retrofit.",
    lead: "The retrofit standard: two certification routes, moisture-safe sequencing, party-wall and embedded-joist challenges, and why 'fabric first, plant last' saves money.",
    publishedISO: "2026-06-09",
    readingMinutes: 13,
    related: ["insulation-retrofit", "external-wall-insulation-cladding", "damp-moisture-condensation", "mvhr-ventilation"],
    external: false,
  },
  {
    slug: "how-sound-travels-in-buildings",
    category: "Soundproofing",
    cluster: "soundproofing",
    title: "How sound travels in buildings — and the four principles of soundproofing.",
    lead: "How sound moves — airborne, impact and flanking — and the four principles that control it: mass, decoupling (isolation), absorption and damping.",
    publishedISO: "2026-07-25",
    readingMinutes: 11,
    related: ["soundproofing-acoustics", "home-health-diagnostics"],
    external: false,
  },
  {
    slug: "airborne-vs-impact-sound",
    category: "Soundproofing",
    cluster: "soundproofing",
    title: "Airborne vs impact sound — the two problems, and how they're rated.",
    lead: "The difference between airborne and impact sound, why they need different treatments, how they're measured (DnT,w + Ctr and L'nT,w), and what Part E requires.",
    publishedISO: "2026-07-26",
    readingMinutes: 10,
    related: ["soundproofing-acoustics", "home-health-diagnostics"],
    external: false,
  },
  {
    slug: "flanking-sound-explained",
    category: "Soundproofing",
    cluster: "soundproofing",
    title: "Flanking sound explained — why soundproofing so often fails.",
    lead: "What flanking transmission is, why it's the commonest reason soundproofing underperforms, the typical flanking paths, and how a whole-system approach addresses it.",
    publishedISO: "2026-07-27",
    readingMinutes: 10,
    related: ["soundproofing-acoustics", "home-health-diagnostics"],
    external: false,
  },
  {
    slug: "soundproofing-walls",
    category: "Soundproofing",
    cluster: "soundproofing",
    title: "Soundproofing walls — independent linings, resilient bars and mass.",
    lead: "The independent (isolated) wall vs resilient-bar systems, adding mass and damping, cavity absorption, sealing, and why flanking and space trade-offs matter.",
    publishedISO: "2026-07-28",
    readingMinutes: 10,
    related: ["soundproofing-acoustics", "home-health-diagnostics"],
    external: false,
  },
  {
    slug: "soundproofing-floors-and-ceilings",
    category: "Soundproofing",
    cluster: "soundproofing",
    title: "Soundproofing floors and ceilings — impact, footsteps and headroom.",
    lead: "Floating floors and resilient layers at source, isolated ceiling systems, the headroom trade-off, why treating the floor above beats the ceiling below, and the flanking factor.",
    publishedISO: "2026-07-29",
    readingMinutes: 11,
    related: ["soundproofing-acoustics", "home-health-diagnostics"],
    external: false,
  },
  {
    slug: "soundproofing-myths-and-mistakes",
    category: "Soundproofing",
    cluster: "soundproofing",
    title: "Soundproofing myths and mistakes — what doesn't work, and why.",
    lead: "Egg boxes and foam, 'acoustic' paint, confusing absorption with insulation, ignoring flanking and gaps, partial treatment — debunked, with what actually works.",
    publishedISO: "2026-07-30",
    readingMinutes: 11,
    related: ["soundproofing-acoustics", "home-health-diagnostics"],
    external: false,
  },
  {
    slug: "whole-house-retrofit-and-pas2035",
    category: "Retrofit & Insulation",
    cluster: "retrofit",
    title: "Whole-house retrofit and PAS 2035 — why a plan beats piecemeal measures.",
    lead: "Why retrofit should be planned whole-house, what PAS 2035 requires, the role of the assessment, coordinator and risk pathways, and how a Medium-Term Improvement Plan avoids costly lock-in.",
    publishedISO: "2026-07-16",
    readingMinutes: 11,
    related: ["insulation-retrofit", "home-health-diagnostics", "mvhr-ventilation"],
    external: false,
  },
  {
    slug: "fabric-first-and-retrofit-sequencing",
    category: "Retrofit & Insulation",
    cluster: "retrofit",
    title: "Fabric first and retrofit sequencing — the right order of works.",
    lead: "Why 'fabric first, plant last' saves money, the correct sequence of works, how to phase a retrofit without lock-in, and why sizing the heat source before reducing demand is the costly mistake.",
    publishedISO: "2026-07-17",
    readingMinutes: 11,
    related: ["insulation-retrofit", "mvhr-ventilation", "blower-door-testing"],
    external: false,
  },
  {
    slug: "internal-vs-external-wall-insulation",
    category: "Retrofit & Insulation",
    cluster: "retrofit",
    title: "Internal vs external wall insulation — choosing safely.",
    lead: "A clear comparison of IWI and EWI: performance, moisture risk, thermal bridging, cost, disruption, space and planning — and how to choose the right one for your solid-wall home.",
    publishedISO: "2026-07-18",
    readingMinutes: 11,
    related: ["insulation-retrofit", "external-wall-insulation-cladding", "damp-moisture-condensation"],
    external: false,
  },
  {
    slug: "external-wall-insulation-explained",
    category: "Retrofit & Insulation",
    cluster: "retrofit",
    title: "External wall insulation (EWI) — how it works and when to use it.",
    lead: "How EWI wraps the structure in a continuous warm blanket to control thermal bridging and moisture, the build-up and finishes, the detailing that makes or breaks it, and when it's the right choice.",
    publishedISO: "2026-07-19",
    readingMinutes: 11,
    related: ["external-wall-insulation-cladding", "insulation-retrofit", "damp-moisture-condensation"],
    external: false,
  },
  {
    slug: "insulating-solid-walls-safely",
    category: "Retrofit & Insulation",
    cluster: "retrofit",
    title: "Insulating solid walls safely — moisture, breathability and risk.",
    lead: "Why solid-wall insulation carries moisture risk, how traditional walls manage water, the role of vapour-open build-ups, wind-driven rain, and why hygrothermal assessment is essential.",
    publishedISO: "2026-07-20",
    readingMinutes: 11,
    related: ["insulation-retrofit", "damp-moisture-condensation", "external-wall-insulation-cladding"],
    external: false,
  },
  {
    slug: "loft-and-roof-insulation",
    category: "Retrofit & Insulation",
    cluster: "retrofit",
    title: "Loft and roof insulation — cold roofs, warm roofs and condensation risk.",
    lead: "Cold-roof vs warm-roof strategies, why ventilation and air-tightness matter, the condensation risk of insulating at ceiling vs rafter level, room-in-roof detailing, and recommended depths.",
    publishedISO: "2026-07-21",
    readingMinutes: 11,
    related: ["insulation-retrofit", "damp-moisture-condensation", "blower-door-testing"],
    external: false,
  },
  {
    slug: "floor-insulation-retrofit",
    category: "Retrofit & Insulation",
    cluster: "retrofit",
    title: "Floor insulation in retrofit — suspended timber and solid floors.",
    lead: "How to insulate ground floors: suspended timber floors and their air-leakage role, solid floor insulation, maintaining underfloor ventilation, air-tightness and the moisture considerations.",
    publishedISO: "2026-07-22",
    readingMinutes: 10,
    related: ["insulation-retrofit", "blower-door-testing", "damp-moisture-condensation"],
    external: false,
  },
  {
    slug: "avoiding-unintended-consequences-retrofit",
    category: "Retrofit & Insulation",
    cluster: "retrofit",
    title: "Avoiding unintended consequences in retrofit — the risks, and how to design them out.",
    lead: "Condensation, mould, moved dew points, new thermal bridges, trapped moisture and overheating — the unintended consequences of poor retrofit, and the diagnose-model-design-verify approach that prevents them.",
    publishedISO: "2026-07-23",
    readingMinutes: 11,
    related: ["insulation-retrofit", "damp-moisture-condensation", "mvhr-ventilation"],
    external: false,
  },
  {
    slug: "piv-positive-input-ventilation",
    category: "Ventilation & IAQ",
    cluster: "ventilation",
    title: "PIV explained — positive input ventilation, and where it fits.",
    lead: "How PIV works, what it's good at (and not), how it compares to MVHR and extract, its lack of heat recovery, and when it's the right ventilation strategy.",
    publishedISO: "2026-07-07",
    readingMinutes: 9,
    related: ["mvhr-ventilation", "indoor-air-quality", "damp-moisture-condensation"],
    external: false,
  },
  {
    slug: "extract-ventilation-dmev-mev",
    category: "Ventilation & IAQ",
    cluster: "ventilation",
    title: "Extract ventilation explained — intermittent, dMEV and MEV.",
    lead: "Intermittent extractor fans, continuous decentralised extract (dMEV), centralised MEV, humidity-sensing controls, Part F, and where each fits in a moisture-control strategy.",
    publishedISO: "2026-07-08",
    readingMinutes: 10,
    related: ["mvhr-ventilation", "damp-moisture-condensation", "indoor-air-quality"],
    external: false,
  },
  {
    slug: "trickle-vents-and-background-ventilation",
    category: "Ventilation & IAQ",
    cluster: "ventilation",
    title: "Trickle vents and background ventilation — what they do, and don't.",
    lead: "What background ventilators are for, how they fit Part F, why people block them and shouldn't, and how background ventilation interacts with airtightness and mechanical systems.",
    publishedISO: "2026-07-09",
    readingMinutes: 9,
    related: ["mvhr-ventilation", "indoor-air-quality", "damp-moisture-condensation"],
    external: false,
  },
  {
    slug: "co2-monitoring-indoor",
    category: "Ventilation & IAQ",
    cluster: "ventilation",
    title: "CO₂ monitoring — the simplest measure of whether you're ventilating enough.",
    lead: "Why indoor CO₂ is the best single proxy for ventilation adequacy, what the ppm thresholds mean, the effect on sleep and cognition, and how to use a CO₂ monitor.",
    publishedISO: "2026-07-10",
    readingMinutes: 9,
    related: ["indoor-air-quality", "mvhr-ventilation", "home-health-diagnostics"],
    external: false,
  },
  {
    slug: "pm25-and-particulates-indoors",
    category: "Ventilation & IAQ",
    cluster: "ventilation",
    title: "PM2.5 and particulates indoors — sources, health and filtration.",
    lead: "What PM2.5 and PM10 are, where indoor particulates come from (cooking, wood burning, candles, outdoor air), their health effects, WHO guideline levels, and how MVHR filtration and source control reduce them.",
    publishedISO: "2026-07-11",
    readingMinutes: 10,
    related: ["indoor-air-quality", "mvhr-ventilation", "home-health-diagnostics"],
    external: false,
  },
  {
    slug: "vocs-and-indoor-pollutants",
    category: "Ventilation & IAQ",
    cluster: "ventilation",
    title: "VOCs and indoor pollutants — formaldehyde, off-gassing and source control.",
    lead: "What VOCs are, common indoor sources (paints, furnishings, MDF/formaldehyde), how they affect health, what TVOC readings mean, and why source control plus ventilation is the answer.",
    publishedISO: "2026-07-12",
    readingMinutes: 10,
    related: ["indoor-air-quality", "mvhr-ventilation", "home-health-diagnostics"],
    external: false,
  },
  {
    slug: "ventilation-and-mould-prevention",
    category: "Ventilation & IAQ",
    cluster: "ventilation",
    title: "Ventilation and mould prevention — the humidity connection.",
    lead: "How ventilation controls indoor humidity, the moisture households generate, the link between ventilation rate and surface RH, and why ventilation plus warm surfaces is the durable mould fix.",
    publishedISO: "2026-07-13",
    readingMinutes: 10,
    related: ["mvhr-ventilation", "damp-moisture-condensation", "indoor-air-quality"],
    external: false,
  },
  {
    slug: "ventilation-strategy-for-retrofit",
    category: "Ventilation & IAQ",
    cluster: "ventilation",
    title: "Ventilation strategy for retrofit — matching ventilation to airtightness.",
    lead: "How to choose the right ventilation as airtightness improves: when extract, PIV or MVHR is appropriate, 'build tight ventilate right', commissioning, Part F, and avoiding the post-retrofit mould trap.",
    publishedISO: "2026-07-14",
    readingMinutes: 11,
    related: ["mvhr-ventilation", "insulation-retrofit", "damp-moisture-condensation"],
    external: false,
  },
  {
    slug: "how-thermal-imaging-works",
    category: "Thermal Imaging",
    cluster: "thermal-imaging",
    title: "How thermal imaging works — emissivity, reflected temperature and resolution.",
    lead: "How a thermal camera measures surface temperature, the role of emissivity and reflected apparent temperature, thermal vs spatial resolution, and the difference between a qualitative and a quantitative survey.",
    publishedISO: "2026-06-29",
    readingMinutes: 11,
    related: ["thermal-imaging", "home-health-diagnostics", "blower-door-testing"],
    external: false,
  },
  {
    slug: "temperature-differential-thermal-survey",
    category: "Thermal Imaging",
    cluster: "thermal-imaging",
    title: "The temperature differential — why a valid thermal survey needs a cold day.",
    lead: "Why thermography needs a sufficient inside-to-outside ΔT, how much is enough, the role of conditioning, weather, solar loading and timing, and the standards behind a valid survey.",
    publishedISO: "2026-06-30",
    readingMinutes: 11,
    related: ["thermal-imaging", "blower-door-testing", "home-health-diagnostics"],
    external: false,
  },
  {
    slug: "thermal-imaging-for-insulation-defects",
    category: "Thermal Imaging",
    cluster: "thermal-imaging",
    title: "Thermal imaging for insulation defects — missing, slumped and bridged.",
    lead: "How thermal imaging reveals missing, slumped and gapped insulation, cavity-fill defects and cold bridging — reading the signatures, and why internal and external surveys complement each other.",
    publishedISO: "2026-07-01",
    readingMinutes: 11,
    related: ["thermal-imaging", "insulation-retrofit", "external-wall-insulation-cladding"],
    external: false,
  },
  {
    slug: "thermal-imaging-for-air-leakage",
    category: "Thermal Imaging",
    cluster: "thermal-imaging",
    title: "Thermal imaging for air leakage — finding draughts under depressurisation.",
    lead: "How thermal imaging detects air leakage when combined with a blower door: the cold 'fingers' of incoming air, how they differ from insulation defects, and why the two techniques belong together.",
    publishedISO: "2026-07-02",
    readingMinutes: 10,
    related: ["thermal-imaging", "blower-door-testing", "mvhr-ventilation"],
    external: false,
  },
  {
    slug: "thermal-imaging-for-condensation-and-damp",
    category: "Thermal Imaging",
    cluster: "thermal-imaging",
    title: "Thermal imaging for condensation and damp — cold surfaces and dew point.",
    lead: "How thermography locates surfaces below the dew point, the evaporative-cooling signature of damp, distinguishing it from missing insulation, and why it's combined with humidity logging and moisture meters.",
    publishedISO: "2026-07-03",
    readingMinutes: 11,
    related: ["thermal-imaging", "damp-moisture-condensation", "home-health-diagnostics"],
    external: false,
  },
  {
    slug: "thermal-imaging-for-retrofit-verification",
    category: "Thermal Imaging",
    cluster: "thermal-imaging",
    title: "Thermal imaging for retrofit verification — proving the work was done right.",
    lead: "Before-and-after surveys, checking insulation continuity and install quality, confirming thermal bridges are designed out, and providing the as-built evidence that closes the performance gap.",
    publishedISO: "2026-07-04",
    readingMinutes: 11,
    related: ["thermal-imaging", "insulation-retrofit", "blower-door-testing"],
    external: false,
  },
  {
    slug: "common-thermal-imaging-mistakes",
    category: "Thermal Imaging",
    cluster: "thermal-imaging",
    title: "Common thermal imaging mistakes — and how to spot a bad survey.",
    lead: "Solar loading, insufficient ΔT, wrong emissivity, reflective surfaces, auto-scaled palettes, thermal-mass lag and over-claiming — plus how to tell a credible thermal survey from a worthless one.",
    publishedISO: "2026-07-05",
    readingMinutes: 11,
    related: ["thermal-imaging", "home-health-diagnostics", "blower-door-testing"],
    external: false,
  },
  {
    slug: "dew-point-explained",
    category: "Building Physics",
    cluster: "building-physics",
    title: "Dew point explained — the temperature where condensation begins.",
    lead: "The single most useful number in moisture diagnostics: what dew point is, the psychrometrics behind it, and how comparing it against surface temperature decides whether a surface stays dry or grows mould.",
    publishedISO: "2026-06-20",
    readingMinutes: 10,
    related: ["damp-moisture-condensation", "thermal-imaging", "home-health-diagnostics"],
    external: false,
  },
  {
    slug: "relative-humidity-explained",
    category: "Building Physics",
    cluster: "building-physics",
    title: "Relative humidity explained — and why 80% at a surface means mould.",
    lead: "What RH actually measures, how it differs from absolute humidity and vapour pressure, why it changes with temperature alone, and the 80% surface-RH threshold that governs mould.",
    publishedISO: "2026-06-21",
    readingMinutes: 10,
    related: ["damp-moisture-condensation", "indoor-air-quality", "thermal-imaging"],
    external: false,
  },
  {
    slug: "vapour-diffusion-and-permeability",
    category: "Building Physics",
    cluster: "building-physics",
    title: "Vapour diffusion and permeability — μ-values, sd-values and 'breathability'.",
    lead: "How vapour diffuses through materials: vapour pressure, the μ resistance factor, the sd-value (equivalent air layer), vapour control layers, and what 'breathable' really means.",
    publishedISO: "2026-06-22",
    readingMinutes: 11,
    related: ["insulation-retrofit", "damp-moisture-condensation", "external-wall-insulation-cladding"],
    external: false,
  },
  {
    slug: "moisture-transport-mechanisms",
    category: "Building Physics",
    cluster: "building-physics",
    title: "Moisture transport mechanisms — the five ways water moves through buildings.",
    lead: "Vapour diffusion, air convection, capillary transport, gravity/bulk water and built-in moisture — five mechanisms, each with a different driver and a different fix, plus hygroscopic sorption.",
    publishedISO: "2026-06-23",
    readingMinutes: 11,
    related: ["damp-moisture-condensation", "thermal-imaging", "home-health-diagnostics"],
    external: false,
  },
  {
    slug: "surface-condensation-and-mould",
    category: "Building Physics",
    cluster: "building-physics",
    title: "Surface condensation and mould — the mechanism, and the real fix.",
    lead: "How surface condensation and mould form: the 80% surface-RH germination threshold, growth isopleths, the fRsi temperature factor, common species and health effects, and why warming surfaces beats bleach.",
    publishedISO: "2026-06-24",
    readingMinutes: 11,
    related: ["damp-moisture-condensation", "thermal-imaging", "indoor-air-quality"],
    external: false,
  },
  {
    slug: "interstitial-condensation-explained",
    category: "Building Physics",
    cluster: "building-physics",
    title: "Interstitial condensation explained — the damp you can't see.",
    lead: "How warm moist air and vapour reach the dew point inside a wall or roof, where the condensation plane forms, why it rots timber and insulation unseen, and how it's designed out.",
    publishedISO: "2026-06-25",
    readingMinutes: 11,
    related: ["insulation-retrofit", "damp-moisture-condensation", "thermal-imaging"],
    external: false,
  },
  {
    slug: "condensation-risk-analysis-glaser-wufi",
    category: "Building Physics",
    cluster: "building-physics",
    title: "Condensation risk analysis — Glaser vs WUFI hygrothermal modelling.",
    lead: "How interstitial condensation risk is predicted before building: the steady-state Glaser method (BS EN ISO 13788), its limitations, and transient WUFI simulation — and when each is needed for moisture-safe retrofit.",
    publishedISO: "2026-06-26",
    readingMinutes: 12,
    related: ["insulation-retrofit", "external-wall-insulation-cladding", "damp-moisture-condensation"],
    external: false,
  },
  {
    slug: "moisture-measurement-methods",
    category: "Building Physics",
    cluster: "building-physics",
    title: "Moisture measurement methods — meters, loggers and their limitations.",
    lead: "What resistance and capacitance meters really measure, the false readings from hygroscopic salts, the carbide and gravimetric reference tests, RH data loggers, interstitial probes and thermal imaging.",
    publishedISO: "2026-06-27",
    readingMinutes: 11,
    related: ["damp-moisture-condensation", "thermal-imaging", "home-health-diagnostics"],
    external: false,
  },
  {
    slug: "what-is-airtightness-ach50",
    category: "Airtightness",
    cluster: "airtightness",
    title: "What is airtightness? ACH₅₀, air permeability and 50 Pa explained.",
    lead: "What airtightness really means, the difference between ACH₅₀ and air permeability, what 50 pascals represents, and why air leakage drives heat loss, condensation and poor air quality.",
    publishedISO: "2026-06-11",
    readingMinutes: 11,
    related: ["blower-door-testing", "home-health-diagnostics", "insulation-retrofit"],
    external: false,
  },
  {
    slug: "how-the-blower-door-test-works",
    category: "Airtightness",
    cluster: "airtightness",
    title: "How a blower door test works — methodology, equipment and standards.",
    lead: "The fan and door panel, the manometer, pressurisation vs depressurisation, single-point vs multi-point (regression) tests, BS EN ISO 9972 and ATTMA, preparation and sources of error.",
    publishedISO: "2026-06-12",
    readingMinutes: 13,
    related: ["blower-door-testing", "home-health-diagnostics", "thermal-imaging"],
    external: false,
  },
  {
    slug: "interpreting-blower-door-results",
    category: "Airtightness",
    cluster: "airtightness",
    title: "Interpreting blower door results — ACH₅₀, q50 and what 'good' looks like.",
    lead: "How to read an airtightness result: ACH₅₀ vs air permeability, UK benchmarks, Passive House and EnerPHit targets, the flow exponent, equivalent leakage area, and pass vs genuinely good.",
    publishedISO: "2026-06-13",
    readingMinutes: 11,
    related: ["blower-door-testing", "home-health-diagnostics", "insulation-retrofit"],
    external: false,
  },
  {
    slug: "uk-airtightness-standards-and-regulations",
    category: "Airtightness",
    cluster: "airtightness",
    title: "UK airtightness standards and regulations — Part L, ATTMA and Passive House.",
    lead: "Approved Document L air-permeability limits, mandatory testing and ATTMA registration, the Part F ventilation link, and how Building Regs compare to Passive House (0.6 ACH₅₀) and EnerPHit.",
    publishedISO: "2026-06-14",
    readingMinutes: 11,
    related: ["blower-door-testing", "mvhr-ventilation", "home-health-diagnostics"],
    external: false,
  },
  {
    slug: "common-air-leakage-pathways",
    category: "Airtightness",
    cluster: "airtightness",
    title: "Common air-leakage pathways in UK homes — where buildings actually leak.",
    lead: "Service penetrations, loft hatches, floor-to-wall junctions, suspended timber floors, skirtings, sockets, chimneys, intermediate floor voids and party-wall bypass — mapped element by element.",
    publishedISO: "2026-06-15",
    readingMinutes: 10,
    related: ["blower-door-testing", "insulation-retrofit", "thermal-imaging"],
    external: false,
  },
  {
    slug: "smoke-tracing-and-leak-detection",
    category: "Airtightness",
    cluster: "airtightness",
    title: "Smoke tracing and leak detection — finding exactly where a building leaks.",
    lead: "Smoke pencils, puffers and foggers under depressurisation, anemometer airflow measurement, thermal imaging of incoming cold air, and how leaks are documented into an actionable sealing plan.",
    publishedISO: "2026-06-16",
    readingMinutes: 10,
    related: ["blower-door-testing", "thermal-imaging", "home-health-diagnostics"],
    external: false,
  },
  {
    slug: "airtightness-strategy-and-detailing",
    category: "Airtightness",
    cluster: "airtightness",
    title: "Airtightness strategy and detailing — the continuous air barrier.",
    lead: "The continuous air barrier and the 'red pen' rule, choosing the airtight layer, detailing junctions and penetrations, the service void, sequencing and the airtightness champion.",
    publishedISO: "2026-06-17",
    readingMinutes: 12,
    related: ["blower-door-testing", "insulation-retrofit", "mvhr-ventilation"],
    external: false,
  },
  {
    slug: "airtightness-and-condensation-risk",
    category: "Airtightness",
    cluster: "airtightness",
    title: "Airtightness and condensation risk — why leaky buildings rot from the inside.",
    lead: "Air-transported moisture vs vapour diffusion, exfiltration condensation, and why an airtight, well-ventilated envelope is the moisture-safe one — plus the dangerous mistake of sealing without ventilating.",
    publishedISO: "2026-06-18",
    readingMinutes: 11,
    related: ["blower-door-testing", "damp-moisture-condensation", "mvhr-ventilation"],
    external: false,
  },
  {
    slug: "why-damp-treatments-fail",
    category: "Damp & Moisture",
    title: "Why most damp treatments fail — and what actually works.",
    lead: "Injected damp-proof courses, chemical creams and waterproof renders rarely solve the problem because they treat the symptom, not the cause.",
    publishedISO: "2026-02-15",
    readingMinutes: 8,
    href: "/learn/why-damp-treatments-fail",
    related: ["damp-moisture-condensation", "thermal-imaging"],
    external: false,
  },
  {
    slug: "condensation-vs-rising-damp",
    category: "Damp & Moisture",
    title: "Condensation vs damp — how to tell the difference (with measured data).",
    lead: "The single most misdiagnosed problem in UK homes. Here's how building physics — not guesswork — tells the two apart.",
    publishedISO: "2026-02-20",
    readingMinutes: 9,
    related: ["damp-moisture-condensation", "thermal-imaging", "indoor-air-quality"],
    external: false,
  },
  {
    slug: "what-a-blower-door-test-tells-you",
    category: "Airtightness",
    title: "What a blower door test actually tells you about your house.",
    lead: "ACH₅₀, m³/h, smoke trails and thermal-under-depressurisation — the four outputs of a proper test and what each one means for your retrofit.",
    publishedISO: "2026-02-25",
    readingMinutes: 10,
    related: ["blower-door-testing", "mvhr-ventilation"],
    external: false,
  },
  {
    slug: "thermal-imaging-what-it-can-and-cant-see",
    category: "Thermal Imaging",
    title: "Thermal imaging — what it can (and can't) see.",
    lead: "Used properly, a FLIR survey is the single highest-value diagnostic tool we own. Used badly, it produces beautiful images that mean nothing.",
    publishedISO: "2026-03-02",
    readingMinutes: 9,
    related: ["thermal-imaging", "home-health-diagnostics"],
    external: false,
  },
  {
    slug: "internal-wall-insulation-without-causing-mould",
    category: "Insulation",
    title: "Internal Wall Insulation — how to upgrade a solid wall without causing hidden mould.",
    lead: "IWI is the single highest-risk retrofit measure. Done badly, it creates interstitial condensation behind the new lining. Done properly, it transforms comfort.",
    publishedISO: "2026-03-08",
    readingMinutes: 12,
    related: ["insulation-retrofit", "damp-moisture-condensation"],
    external: false,
  },
  {
    slug: "soundproofing-flats-without-losing-headroom",
    category: "Soundproofing",
    title: "Soundproofing a flat without losing headroom (or your sanity).",
    lead: "Mass, decoupling, absorption — the three things every effective soundproofing build-up needs, and the flanking paths that quietly ruin the result.",
    publishedISO: "2026-03-14",
    readingMinutes: 11,
    related: ["soundproofing-acoustics"],
    external: false,
  },
  {
    slug: "mvhr-vs-positive-input-ventilation",
    category: "Ventilation",
    title: "MVHR vs PIV — which ventilation strategy is right for your home?",
    lead: "MVHR, PIV, dMEV and intermittent extract — four ventilation strategies with very different running costs, performance and suitability.",
    publishedISO: "2026-03-20",
    readingMinutes: 10,
    related: ["mvhr-ventilation", "indoor-air-quality", "blower-door-testing"],
    external: false,
  },
  {
    slug: "indoor-air-quality-the-numbers-that-matter",
    category: "Indoor Air Quality",
    title: "Indoor air quality — the four numbers that actually matter.",
    lead: "CO₂, humidity, PM2.5 and VOCs. Get these four right and almost every other 'air-quality' worry resolves itself.",
    publishedISO: "2026-03-26",
    readingMinutes: 8,
    related: ["indoor-air-quality", "mvhr-ventilation"],
    external: false,
  },

  // ---- Problem-based home guides (cluster: home-problems) ----
  { slug: "why-is-my-house-cold", category: "Home Problems", cluster: "home-problems", title: "Why is my house cold? The real reasons — and how to find them.", lead: "Heat is escaping faster than you can replace it. Here is how to measure where it's going.", publishedISO: "2026-06-12", readingMinutes: 8, related: ["home-health-diagnostics", "thermal-imaging", "blower-door-testing", "insulation-retrofit"], external: false },
  { slug: "why-are-my-walls-cold", category: "Home Problems", cluster: "home-problems", title: "Why are my walls cold to the touch?", lead: "Cold walls lose heat and risk condensation — both measurable, both fixable.", publishedISO: "2026-06-12", readingMinutes: 7, related: ["thermal-imaging", "insulation-retrofit", "external-wall-insulation-cladding", "damp-moisture-condensation"], external: false },
  { slug: "why-is-one-room-colder", category: "Home Problems", cluster: "home-problems", title: "Why is one room colder than the rest of the house?", lead: "One persistently cold room almost always has a specific, fixable cause.", publishedISO: "2026-06-13", readingMinutes: 7, related: ["thermal-imaging", "heat-loss-investigation", "insulation-retrofit", "blower-door-testing"], external: false },
  { slug: "cold-floors-in-winter", category: "Home Problems", cluster: "home-problems", title: "Cold floors in winter — why they happen and how to fix them.", lead: "Usually an uninsulated suspended floor over a cold, draughty void — and very fixable.", publishedISO: "2026-06-13", readingMinutes: 7, related: ["insulation-retrofit", "thermal-imaging", "blower-door-testing", "home-health-diagnostics"], external: false },
  { slug: "why-is-my-house-hard-to-heat", category: "Home Problems", cluster: "home-problems", title: "Why is my house so hard to heat?", lead: "Slow to warm and quick to cool means high heat loss — reduce it before upgrading heating.", publishedISO: "2026-06-14", readingMinutes: 8, related: ["home-health-diagnostics", "blower-door-testing", "insulation-retrofit", "heat-pump-readiness"], external: false },
  { slug: "high-energy-bills-house-still-cold", category: "Home Problems", cluster: "home-problems", title: "High energy bills but the house is still cold — what's going on?", lead: "You're paying to heat the outside. Here's how to find and stop the loss.", publishedISO: "2026-06-14", readingMinutes: 8, related: ["home-health-diagnostics", "blower-door-testing", "insulation-retrofit", "heat-loss-investigation"], external: false },
  { slug: "why-does-my-home-feel-draughty", category: "Home Problems", cluster: "home-problems", title: "Why does my home feel draughty?", lead: "Draughts are uncontrolled air leakage — invisible, but entirely measurable.", publishedISO: "2026-06-15", readingMinutes: 7, related: ["blower-door-testing", "home-health-diagnostics", "mvhr-ventilation", "insulation-retrofit"], external: false },
  { slug: "air-leakage-around-windows-and-doors", category: "Home Problems", cluster: "home-problems", title: "Air leakage around windows and doors — the hidden draught.", lead: "Even new windows leak around the frame perimeter. Here's how we find and seal it.", publishedISO: "2026-06-15", readingMinutes: 6, related: ["blower-door-testing", "thermal-imaging", "insulation-retrofit", "home-health-diagnostics"], external: false },
  { slug: "condensation-on-windows", category: "Home Problems", cluster: "home-problems", title: "Condensation on windows — what it means and how to stop it.", lead: "Streaming windows are high indoor humidity meeting cold glass — a warning, not just a nuisance.", publishedISO: "2026-06-16", readingMinutes: 7, related: ["damp-moisture-condensation", "mvhr-ventilation", "indoor-air-quality", "thermal-imaging"], external: false },
  { slug: "damp-around-window-frames", category: "Home Problems", cluster: "home-problems", title: "Damp around window frames — condensation, leak or cold reveal?", lead: "Telling condensation on a cold reveal from water ingress is the difference between the right fix and a wasted one.", publishedISO: "2026-06-16", readingMinutes: 7, related: ["damp-moisture-condensation", "thermal-imaging", "insulation-retrofit", "home-health-diagnostics"], external: false },
  { slug: "mould-in-corners-of-rooms", category: "Home Problems", cluster: "home-problems", title: "Mould in the corners of rooms — why it always starts there.", lead: "Corners are the coldest spot in the room — a geometric thermal bridge that condenses and grows mould.", publishedISO: "2026-06-17", readingMinutes: 7, related: ["damp-moisture-condensation", "thermal-imaging", "insulation-retrofit", "mvhr-ventilation"], external: false },
  { slug: "mould-behind-wardrobes", category: "Home Problems", cluster: "home-problems", title: "Mould behind wardrobes and furniture — the cold-wall trap.", lead: "Furniture against a cold external wall creates a cold, still, humid micro-climate — perfect for mould.", publishedISO: "2026-06-17", readingMinutes: 6, related: ["damp-moisture-condensation", "thermal-imaging", "insulation-retrofit", "mvhr-ventilation"], external: false },
  { slug: "black-mould-in-bedrooms", category: "Home Problems", cluster: "home-problems", title: "Black mould in bedrooms — why bedrooms are the worst affected.", lead: "Occupants, cool nights, cold walls and closed doors make bedrooms the prime spot for condensation and mould.", publishedISO: "2026-06-18", readingMinutes: 8, related: ["damp-moisture-condensation", "mvhr-ventilation", "indoor-air-quality", "insulation-retrofit"], external: false },
  { slug: "why-does-moisture-keep-returning", category: "Home Problems", cluster: "home-problems", title: "Why does the damp or mould keep coming back?", lead: "Recurring damp means the cause was never diagnosed — only the symptom was treated.", publishedISO: "2026-06-18", readingMinutes: 7, related: ["damp-moisture-condensation", "thermal-imaging", "home-health-diagnostics", "mvhr-ventilation"], external: false },
  { slug: "why-does-my-house-smell-damp", category: "Home Problems", cluster: "home-problems", title: "Why does my house smell damp or musty?", lead: "A musty smell is your nose detecting hidden moisture and microbial growth. Find the source, lose the smell.", publishedISO: "2026-06-19", readingMinutes: 7, related: ["damp-moisture-condensation", "thermal-imaging", "indoor-air-quality", "home-health-diagnostics"], external: false },
  { slug: "loft-insulation-problems", category: "Home Problems", cluster: "home-problems", title: "Loft insulation problems — why a 'fully insulated' loft still loses heat.", lead: "Gaps, compression, eaves shortfalls and a leaky hatch undo much of the benefit — invisibly.", publishedISO: "2026-06-19", readingMinutes: 7, related: ["insulation-retrofit", "thermal-imaging", "blower-door-testing", "home-health-diagnostics"], external: false },
  { slug: "condensation-in-loft-spaces", category: "Home Problems", cluster: "home-problems", title: "Condensation in loft spaces — wet timbers, mould and dripping felt.", lead: "Warm moist air leaking up meets a cold roof and condenses. Seal the air, keep the loft ventilated.", publishedISO: "2026-06-20", readingMinutes: 7, related: ["damp-moisture-condensation", "blower-door-testing", "mvhr-ventilation", "home-health-diagnostics"], external: false },
  { slug: "mould-after-new-windows", category: "Home Problems", cluster: "home-problems", title: "Mould after new windows — why upgrading glazing can backfire.", lead: "Tighter windows removed the old accidental ventilation; the trapped moisture had to go somewhere.", publishedISO: "2026-06-20", readingMinutes: 7, related: ["mvhr-ventilation", "damp-moisture-condensation", "indoor-air-quality", "blower-door-testing"], external: false },

  // ---- Heat Pump Readiness cluster ----
  { slug: "is-my-home-ready-for-a-heat-pump", category: "Heat Pump Readiness", cluster: "heat-pump-readiness", title: "Is my home ready for a heat pump?", lead: "Readiness means low heat loss and emitters that can deliver heat gently — not just the heat pump itself.", publishedISO: "2026-06-21", readingMinutes: 8, related: ["heat-pump-readiness", "home-health-diagnostics", "blower-door-testing", "insulation-retrofit"], external: false },
  { slug: "heat-loss-survey-before-a-heat-pump", category: "Heat Pump Readiness", cluster: "heat-pump-readiness", title: "Why you need a heat loss survey before a heat pump.", lead: "A measured heat loss survey sizes the system correctly and prevents an oversized, inefficient heat pump.", publishedISO: "2026-06-21", readingMinutes: 7, related: ["heat-pump-readiness", "heat-loss-investigation", "blower-door-testing", "thermal-imaging"], external: false },
  { slug: "required-insulation-levels-before-heat-pumps", category: "Heat Pump Readiness", cluster: "heat-pump-readiness", title: "What insulation levels do you need before a heat pump?", lead: "There's no single number — what matters is whole-house heat loss low enough for low-temperature operation.", publishedISO: "2026-06-22", readingMinutes: 7, related: ["heat-pump-readiness", "insulation-retrofit", "home-health-diagnostics", "thermal-imaging"], external: false },
  { slug: "airtightness-and-heat-pumps", category: "Heat Pump Readiness", cluster: "heat-pump-readiness", title: "Airtightness and heat pumps — the overlooked half of readiness.", lead: "Air leakage adds directly to heat demand — a blower door test is central to readiness.", publishedISO: "2026-06-22", readingMinutes: 7, related: ["heat-pump-readiness", "blower-door-testing", "mvhr-ventilation", "home-health-diagnostics"], external: false },
  { slug: "ventilation-requirements-for-heat-pumps", category: "Heat Pump Readiness", cluster: "heat-pump-readiness", title: "Ventilation requirements when you fit a heat pump.", lead: "Tightening the home for efficiency makes controlled ventilation essential — build tight, ventilate right.", publishedISO: "2026-06-23", readingMinutes: 7, related: ["heat-pump-readiness", "mvhr-ventilation", "indoor-air-quality", "blower-door-testing"], external: false },
  { slug: "underfloor-heating-and-heat-pumps", category: "Heat Pump Readiness", cluster: "heat-pump-readiness", title: "Underfloor heating and heat pumps — and what to do with radiators.", lead: "Heat pumps love large, low-temperature emitters — but correctly sized radiators can work too.", publishedISO: "2026-06-23", readingMinutes: 7, related: ["heat-pump-readiness", "heat-loss-investigation", "insulation-retrofit", "home-health-diagnostics"], external: false },
  { slug: "why-heat-pumps-perform-poorly", category: "Heat Pump Readiness", cluster: "heat-pump-readiness", title: "Why some heat pumps perform poorly (and how to avoid it).", lead: "Almost always the building wasn't ready, the unit was oversized, or it was run hot like a boiler.", publishedISO: "2026-06-24", readingMinutes: 8, related: ["heat-pump-readiness", "home-health-diagnostics", "blower-door-testing", "insulation-retrofit"], external: false },
  { slug: "common-heat-pump-retrofit-mistakes", category: "Heat Pump Readiness", cluster: "heat-pump-readiness", title: "Common heat pump retrofit mistakes — and how to avoid them.", lead: "Oversizing, skipping the fabric, ignoring airtightness and running too hot — nearly all avoidable before install.", publishedISO: "2026-06-24", readingMinutes: 7, related: ["heat-pump-readiness", "home-health-diagnostics", "blower-door-testing", "insulation-retrofit"], external: false },
  { slug: "building-regulations-and-heat-pumps", category: "Heat Pump Readiness", cluster: "heat-pump-readiness", title: "Building regulations and heat pumps — what you need to know.", lead: "How the rules on heating, ventilation and fabric relate to readiness — and where minimum compliance ends.", publishedISO: "2026-06-25", readingMinutes: 7, related: ["heat-pump-readiness", "mvhr-ventilation", "insulation-retrofit", "home-health-diagnostics"], external: false },
  { slug: "fabric-first-before-heat-pumps", category: "Heat Pump Readiness", cluster: "heat-pump-readiness", title: "Fabric first, before heat pumps — the principle that makes them work.", lead: "Reduce heat loss before sizing the heating and the heat pump runs small, efficient and cheap.", publishedISO: "2026-06-25", readingMinutes: 8, related: ["heat-pump-readiness", "insulation-retrofit", "blower-door-testing", "home-health-diagnostics"], external: false },
];

// =============================================================================
// Before / After thermal pairs — used by the homepage thermal toggle.
// Each pair shows a thermally weak surface before remediation and the same
// surface after IWI / EWI / airtightness improvement, with measured deltas.
// =============================================================================
export const THERMAL_BEFORE_AFTER = [
  {
    label: "Cold reveal — IWI upgrade",
    before: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/cu9spvuc_FLIR0586.jpg",
    after: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/3vyf463l_FLIR1002.jpg",
    beforeLabel: "Before · Cold reveal at 8.4 °C",
    afterLabel: "After · Surface at 17.9 °C",
    delta: "+9.5 °C surface temperature lift after Internal Wall Insulation",
  },
  {
    label: "Missing loft insulation",
    before: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/itsrbw8g_FLIR0610.jpg",
    after: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/8c8cng3n_FLIR1028.jpg",
    beforeLabel: "Before · Ceiling at 11.2 °C",
    afterLabel: "After · Ceiling at 19.6 °C",
    delta: "+8.4 °C lift after loft top-up and airtightness sealing",
  },
  {
    label: "Solid wall — EWI upgrade",
    before: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/jjq31z7v_FLIR0598.jpg",
    after: "https://customer-assets.emergentagent.com/job_thermal-retrofit/artifacts/b2nlithx_FLIR1000.jpg",
    beforeLabel: "Before · External wall at 4.8 °C",
    afterLabel: "After · External wall at 14.1 °C",
    delta: "+9.3 °C internal-surface lift after External Wall Insulation",
  },
];

// =============================================================================
// Project Map data — completed RetrofitIQ jobs across London & the South East.
// Each entry binds a real case-study or area to a postcode + approximate map
// coordinates (lat/lng) for the /projects map. New jobs are appended here.
// Coordinates approximate to postcode-district centre — not exact street level
// (we don't publish client addresses).
// =============================================================================
export const PROJECT_LOCATIONS = [
  {
    id: "francis-ig8",
    postcode: "IG8",
    area: "Woodford Green, East London",
    region: "Greater London",
    lat: 51.6094, lng: 0.0464,
    title: "IWI retrofit & condensation remediation",
    summary: "Full internal wall insulation retrofit on a cold solid-wall Victorian flat. Two pre-works diagnostic visits, hygrothermal-modelled PIR build-up, post-works re-imaging.",
    services: ["Thermal Imaging", "Insulation & Retrofit", "Damp & Condensation"],
    completed: "2024",
    caseStudyTo: "/case-studies",
  },
  {
    id: "luke-n17",
    postcode: "N17",
    area: "Tottenham, North London",
    region: "Greater London",
    lat: 51.5959, lng: -0.0726,
    title: "Whole-house diagnostic survey",
    summary: "Comprehensive Home Health Diagnostic Survey — thermal imaging, blower door, moisture and IAQ — followed by a costed retrofit roadmap.",
    services: ["Home Health Diagnostics", "Thermal Imaging", "Blower Door Testing"],
    completed: "2024",
    caseStudyTo: "/case-studies",
  },
  {
    id: "giuliano",
    postcode: "E2",
    area: "Bethnal Green, East London",
    region: "Greater London",
    lat: 51.5274, lng: -0.0552,
    title: "Diagnostic thermal & moisture survey",
    summary: "Multi-room FLIR survey identifying cold reveals, lintel cold bands and condensation-risk hotspots. Targeted IWI and ventilation remediation specified.",
    services: ["Thermal Imaging", "Damp & Condensation", "MVHR & Ventilation"],
    completed: "2024",
    caseStudyTo: "/case-studies",
  },
  {
    id: "property-assessment",
    postcode: "IG8",
    area: "Woodford Green, East London",
    region: "Greater London",
    lat: 51.6090, lng: 0.0512,
    title: "Property assessment & costed report",
    summary: "Pre-purchase diagnostic survey — thermal imaging, moisture, ventilation and condensation-risk analysis. Costed improvement schedule delivered.",
    services: ["Home Health Diagnostics", "Thermal Imaging"],
    completed: "2024",
    caseStudyTo: "/case-studies",
  },
  {
    id: "brick-cladding-e15",
    postcode: "E15",
    area: "Stratford, East London",
    region: "Greater London",
    lat: 51.5416, lng: 0.0042,
    title: "Ventilated brick-slip cladding system",
    summary: "External envelope retrofit — ventilated brick-slip cladding over mineral wool insulation on a London residential block. Prefabricated panels, rail-mounted, mortar pointed.",
    services: ["External Wall Insulation & Cladding"],
    completed: "2024",
    caseStudyTo: "/case-studies",
  },
  {
    id: "n7-soundproofing",
    postcode: "N7",
    area: "Holloway, North London",
    region: "Greater London",
    lat: 51.5526, lng: -0.1209,
    title: "Acoustic upgrade — top-floor flat",
    summary: "Top-floor flat with chronic footfall and TV bleed-through. Measured baseline, decoupled mass-spring-mass ceiling and acoustic floor build-up. Re-tested post-works.",
    services: ["Soundproofing & Acoustics"],
    completed: "2024",
    caseStudyTo: "/case-studies",
  },
];
